<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="backButtonRoute ? true : false"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
    :tabs="[]"
    :selectedIndex="undefined"
    ref="gc-base-layout"
    class="invoice-v2"
  >
    <slot> </slot>
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Sales Order'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="
          baseView.mode == 'create'
            ? 'Create Invoice'
            : baseView.mode == 'edit'
            ? 'Edit Invoice # ' + baseView.id
            : 'Invoice # ' + baseView.id
        "
        :secondaryLabel="
          baseView.mode != 'create' ? model.CustomerName : undefined
        "
        v-if="loaded"
      />
    </template>
    <div class="css-v2 transaction-screen" v-if="loaded">
      <div class="gc-header-toolbar">
        <gc-form-header-toolbar
          :toolbar-options="getHeaderToolbar"
          :data-model="model"
          :itemSaveCallBack="checkHeaderFormValidation"
          :backRoute="backButtonRoute"
          :setLoading="showLoading"
          ref="gc-form-header-toolbar"
          :baseView="baseView"
        ></gc-form-header-toolbar>
      </div>

      <div class="gc-header-form" v-show="!gridExpand">
        <div class="header-form-container">
          <div class="header-form">
            <div class="view-card responsive-paddings">
              <gc-form
                :formOptions="{
                  gcLabelLocation: 'left',
                }"
                :formItems="getHeaderFormItems"
                :isNormal="true"
                :data-model="model"
                :deliveryAddressClicked="showDeliveryAddress"
                :formMode="baseView.mode"
                :fromFrom="'gc-header-form'"
                ref="gc-header-form"
                :valueChangeCallBack="headerFormValueChanged"
                :checkValid="isValid"
                :validationGroup="'gc-header-form'"
                :scrollingEnabled="false"
                :formContentReady="onHeaderFormContentReady"
              ></gc-form>
            </div>
          </div>
          <div class="header-comments">
            <div class="view-card responsive-paddings">
              <gc-form
                :formOptions="{
                  gcLabelLocation: 'left',
                }"
                :formItems="commentPopUpItems"
                :isNormal="true"
                :data-model="commentModel"
                :deliveryAddressClicked="undefined"
                :formMode="baseView.mode"
                :fromFrom="'gc-header-comments'"
                ref="gc-header-comments"
                :valueChangeCallBack="headerFormValueChanged"
                :checkValid="isValid"
                :validationGroup="'gc-header-comments'"
                :scrollingEnabled="true"
                :formHeight="headerDxFormHeight"
              ></gc-form>
            </div>
          </div>
        </div>
      </div>

      <div class="gc-details-grid">
        <dx-data-grid
          @toolbar-preparing="toolbarPreparing($event)"
          :columns="getGridColumns"
          :data-source="getDataSource"
          :height="getGridHeight"
          @cell-prepared="onCellPrepared"
          :ref="'dx-data-grid-comp'"
          :key-expr="'ID'"
          :key="soDetailsCurrentView"
          @content-ready="onContentReady"
          @editor-preparing="onEditorPreparing"
          :twoWayBindingEnabled="false"
          @editing-start="onEditingStart"
          :repaintChangesOnly="true"
          :columnChooser="{
            enabled: false,
          }"
          @row-collapsed="onRowCollapsed"
          @row-expanded="onRowExpanded"
          :focusedRowEnabled="baseView.mode === 'create'"
        >
          <DxLoadPanel :show-indicator="true" :enabled="true" />
          <DxPaging :page-size="50" :enabled="false" />
          <DxPager :visible="false" />

          <DxScrolling
            :mode="'virtual'"
            :row-rendering-mode="'virtual'"
            :preload-enabled="true"
            :renderAsync="false"
            :useNative="false"
          />

          <DxEditing
            :allow-updating="baseView.mode === 'create'"
            :allow-deleting="false"
            mode="cell"
          />

          <DxKeyboardNavigation
            :edit-on-key-press="true"
            :enter-key-action="'startEdit'"
            :enter-key-direction="'none'"
            :enabled="true"
          />

          <DxStateStoring
            :enabled="false"
            :storage-key="getPersistenceKey"
            type="localStorage"
          />

          <DxMasterDetail
            :enabled="getChildGridColumns && getChildGridColumns.length > 0"
            template="masterDetailTemplate"
          />
          <template #masterDetailTemplate="{ data: lineitem }">
            <dx-data-grid
              :data-source="lineitem.data.InvoiceProductBatches"
              :columns="getChildGridColumns"
              :columnChooser="{
                enabled: false,
              }"
              :searchPanel="{
                visible: false,
              }"
              :pager="{ visible: false }"
              @editor-preparing="onBatchEditorPreparing($event, lineitem)"
              @row-removed="onBatchRowRemoved"
              @cell-prepared="onBatchCellPrepared"
            >
              <DxEditing
                :allow-updating="baseView.mode === 'create'"
                :allow-deleting="baseView.mode === 'create'"
                mode="cell"
                :select-text-on-edit-start="true"
                :refreshMode="'reshape'"
                :new-row-position="'last'"
              />

              <template #Record="{ data }">
                <GcRecord
                  :data="data"
                  :primary="data.column['cellTemplateRecord']['primary']"
                  :secondary="data.column['cellTemplateRecord']['secondary']"
                />
              </template>
              <template #Date="{ data }">
                <GcDate
                  :date="data.data[data.column.dataField]"
                  :valueChangeCallBack="valueChangeCallBack"
                  :columnData="data.column"
                  :gridData="data"
                  :readOnly="
                    data.column.makeReadOnly
                      ? data.column.makeReadOnly(data)
                      : baseView.mode == 'review'
                  "
                />
              </template>
              <template #Decimal="{ data }">
                <GcDecimal :dataValue="data" :format="data.column.format" />
              </template>
            </dx-data-grid>
          </template>

          <template #Record="{ data }">
            <GcRecord
              :data="data"
              :primary="data.column['cellTemplateRecord']['primary']"
              :secondary="data.column['cellTemplateRecord']['secondary']"
            />
          </template>

          <template #Decimal="{ data }">
            <GcDecimal :dataValue="data" :format="data.column.format" />
          </template>

          <template #menu-cell-template="{ data }">
            <DxDropDownButton
              :items="[
                { menuText: 'Line Comment', data: data },
                {
                  menuText: 'Item History',
                  data: data,
                  visible: baseView.mode != 'create',
                },
                {
                  menuText: 'GP Tool',
                  data: data,
                  visible: baseView.mode == 'edit' && gpToolVisible,
                },
                {
                  menuText: 'Copy Line',
                  data: data,
                  visible: baseView.mode == 'edit',
                },
              ]"
              keyExpr="menuText"
              displayExpr="menuText"
              :drop-down-options="{ width: 250 }"
              class="gc-dropdown-icon-button"
              :id="data.data.ID"
              :icon="'overflow'"
              styling-mode="text"
              :show-arrow-icon="false"
            />
          </template>

          <template #EditableDecimal="{ data }">
            <div>
              <DxNumberBox
                :value="data.value"
                format="##,###,###,###,##0"
                class="gc-editable-cell-color"
                @focus-in="
                  (e) => {
                    e.event.target.select();
                    data.column.editorOptions.onFocusIn(e, data);
                  }
                "
                @key-down="
                  (e) => {
                    if (e.event.key == 'Tab' && e.event.shiftKey == true) {
                      data.column.editorOptions.onKeyDown(e, data);
                    }
                  }
                "
                @value-changed="onQuantityChanged($event, data)"
              />
            </div>
          </template>
        </dx-data-grid>
      </div>
      <GcFooter
        v-if="showFooter"
        v-bind:class="selectedIndex == 0 ? 't-so-foolter-h' : 't-so-foolter-d'"
      >
        <template #leftContent>
          <div
            class="gc-grid-footer-item left-content"
            v-for="item in footerLeftItems"
            :key="item.label"
          >
            <label class="gc-footer-label">{{ item.label }}</label>

            <label
              v-if="item.fieldType == 'custome-string'"
              class="gc-footer-value"
              >{{ item.value }}</label
            >
            <GcFooterItem
              :value="item.value"
              :format="item.format"
              :length="item.format == 'Decimal-16-4C' ? 8 : 4"
              v-else
            />
          </div>
        </template>
        <template #rightContent>
          <div
            class="gc-grid-footer-item"
            v-for="item in footerRightItems"
            :key="item.label"
          >
            <label class="gc-footer-label">{{ item.label }}</label>
            <GcFooterItem
              :value="item.value"
              :format="item.format"
              :length="item.format == 'Decimal-16-4C' ? 8 : 6"
            />
          </div>
        </template>
      </GcFooter>
    </div>

    <!-- Delivery Address -->
    <gc-popup
      :popup-visible="editDeliveryAddressVisible"
      popup-title="Deliver To"
      :back-button-function="closeDeliveryAddressPopup"
      :popup-height="375"
      :saveButtonConfig="deliveryAddressPopupConfig.save"
      :backButtonConfig="deliveryAddressPopupConfig.back"
      :wrapperAttr="{ class: 'css-v2 gc-popup' }"
    >
      <div class="css-v2 gc-popup-form">
        <gc-form
          :formOptions="{
            gcLabelLocation: 'left',
          }"
          :formItems="getDeliverToFormItems"
          :isNormal="true"
          :data-model="DeliveryAddressModel"
          :formMode="baseView.mode"
          :fromFrom="'gc-header-form-d-a'"
          :formHeight="240"
          :validationGroup="'gc-header-form-d-a'"
        ></gc-form>
      </div>
    </gc-popup>
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import DataGridMixin from "@/_mixins/data-grid.mixin";
import { getFactorByCode, getGlobalField } from "../../_store/helpers";
import { CommonStorage, FeatureStorage } from "../../_services/storage";
import validationEngine from "devextreme/ui/validation_engine";
import { mapGetters } from "vuex";
import { SalesOrderV3Service } from "../../_services/api/sales/sales-order-v3.service";
import ArrayStore from "devextreme/data/array_store";
import appConfig from "../../app-config";

import { InvoiceService } from "../../_services/api/sales/invoice.service";
import { CompanyService } from "../../_services/api/accounts/company.service";
import { DefaultCompanyBranchDivisionService } from "../../_services/api/sales/default-company-branch-dicision.service";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";
import { InvoiceCreditService } from "../../_services/api/sales/invoice-credit.service";
import { ProductService } from "../../_services/api/products/product.service";
import { alert } from "devextreme/ui/dialog";
import salesFormulas from "../../_helpers/formulas/sales-formulas";
import { SalesOrderState } from "../../_models/sales-order";
import { ProductBatchService } from "../../_services/api/inventory/product-batch.service";
import { AlmondcoDespatchDataService } from "../../_services/api/sales/almondco-despatch-data.service";
import { CustomerAgentService } from "../../_services/api/customers/customer-agent.service";
import { SalesRepresentativeService } from "../../_services/api/sales/sales-rep.service";
import { CustomerService } from "../../_services/api/customers/customer.service";
import SettingsService from "../../_services/api/settings/graycorp/settings.service";
import { TaxCategoryService } from "../../_services/api/settings/accounting/tax-category.service";
import { CustomerDeliveryAddressService } from "../../_services/api/customers/customer-delivery-address.service";
import { SalesOrderService } from "../../_services/api/sales/sales-order.service";
import { CompanyCurrencyService } from "../../_services/api/accounts/company-currency.service";

const {
  calculateGrossProfitPcnt,
  calculateNetLineTotal,
  calculateWetLineTotal,
  calculateTaxableLineTotal,
  calculateTaxLineTotal,
  calculateLineTotal,
  calculateSalesUMNetFXPrice,
  calculateStockUMPrice,
  calculateStockUMNetPrice,
  calculateStockUMFXPrice,
  calculateStockUMQty,
  calculateStockUMNetFXPrice,
  calculateDiscLineTotal,
  calculateDiscFXLineTotal,
  calculateNetFXLineTotal,
  calculateWetFXLineTotal,
  calculateTaxableFXLineTotal,
  calculateTaxFXLineTotal,
  calculateFXLineTotal,
  calculateSalesUMNetPriceFromSalesUMNetFXPrice,
  calculateSalesUMPriceFromSalesUMFXPrice,
  calculateCalculatedDiscPcnt,
} = salesFormulas;

let soDetailsBaseRecord = {
  AddDiscPriceGroupCode: null,
  AddDiscPricingMethod: null,
  AddDiscPricingMethodValue: 0,
  AddDiscSalesUMPrice: 0,
  AddDiscStockUMPrice: 0,
  CalculatedDiscPcnt: 0,
  Comment: "",
  CustomerProduct: "",
  CustomerSalesContractCode: null,
  CustomerSalesContractLineNo: null,
  DiscFXLineTotal: 0,
  DiscLineTotal: 0,
  DiscPcnt: 0,
  ExTaxUnitCost: 0,
  FXLineTotal: 0,
  IsCommentVisible: false,
  IsPrimeCostItem: false,
  ItemCode: "",
  ItemType: "",
  KitVersion: 0,
  LineNo: 0,
  LineTotal: 0,
  NetFXLineTotal: 0,
  NetLineTotal: 0,
  PriceGroupCode: null,
  PriceGroupSalesUMPrice: 0,
  PriceGroupStockUMPrice: 0,
  PricingMethod: null,
  PricingMethodValue: null,
  RecordId: "00000000-0000-0000-0000-000000000000",
  RequiredByDate: null,
  SalesTaxPcnt: 0,
  SalesUM: "",
  SalesUMConversion: null,
  SalesUMCost: 0,
  SalesUMFXPrice: 0,
  SalesUMIsVariable: null,
  SalesUMManufacturedCost: 0,
  SalesUMNetFXPrice: 0,
  SalesUMNetPrice: 0,
  SalesUMPrice: 0,
  SalesUMPromotionNetPrice: 0,
  SalesUMQty: 0.0,
  SalesUMQtyFocused: false,
  SalesUMStandardCost: 0,
  SalesUMStandardPrice: 0,
  ShipDate: null,
  Status: "A",
  StockUM: null,
  StockUMFXPrice: 0,
  StockUMManufacturedCost: 0,
  StockUMNetFXPrice: 0,
  StockUMNetPrice: 0,
  StockUMPrice: 0,
  StockUMQty: 0,
  StockUMStandardCost: 0,
  StockUMStandardPrice: 0,
  TaxableFXLineTotal: 0,
  TaxableLineTotal: 0,
  TaxCode: null,
  TaxFXLineTotal: 0,
  TaxLineTotal: 0,
  WetFreightAdjustment: 0,
  WetFXFreightAdjustment: 0,
  WetFXLineTotal: 0,
  WetLineTotal: 0,
  WetPcnt: 0,
  HasPromotion: false,
  BestBeforeDate: null,
  ProductSizeCode: "",
  gpprcnt: 0,
  SalesUMSuppliedQty: 0,
  customerContractItemsForProduct: undefined,
};

export default {
  mixins: [RouteMixin, FormMixin, DataGridMixin],
  components: {},
  data() {
    return {
      freightCharges: [],
      TransitDays: 0,
      isExchangeReverting: false,
      gridExpand: false,
      timeOutID: undefined,
      priceUpdateTimeOut: undefined,
      discountTimeOut: undefined,
      loaded: false,
      setLoading: false,
      model: {
        Status: "N",
        DateIn: new Date(),
        DateDue: new Date(),
        OrderNo: undefined,
        AgentName: undefined,
        AgentCommissionPcnt: undefined,

        CustomerDeliveryAddressLineNo: undefined,

        BranchCode: null,
        BranchDescription: null,
        DivisionCode: null,
        DivisionDescription: null,
        SalesOrderTypeCode: null,
        SalesOrderTypeDescription: null,
        SalesRepresentativeCode: null,
        SalesRepresentativeFullName: null,

        CurrencyCode: "",
        ExchangeRate: 1,

        CompanyCurrency: undefined,

        DateChanged: null,
        DateCreated: null,
        ChangedBy: null,
        CreatedBy: null,
        SalesOrderDateChanged: undefined,
        SalesOrderCode: undefined,

        CustomerCode: undefined,
        RequiredByDate: null,
        ShipDate: null,
      },
      DeliveryAddressModel: {
        DeliveryAddressCompanyName: null,
        DeliveryAddressCountry: null,
        DeliveryAddressPostcode: null,
        DeliveryAddressState: null,
        DeliveryAddressStreetLine1: null,
        DeliveryAddressStreetLine2: null,
        DeliveryAddressSuburb: null,
        CustomerDeliveryAddressLineNo: undefined,
      },
      commentModel: {},
      selectedIndex: 0,

      popupVisible: false,
      popupConfig: undefined,
      isValid: false,
      editDeliveryAddressVisible: false,
      addressData: undefined,
      soDetailsCurrentView: "CustomerList",
      showFooter: true,
      footerItems: [],
      changes: [],
      localDataSource: new ArrayStore({
        key: "LineNo",
        data: [],
      }),
      showLeftContainer: true,
      showRightContainer: false,

      selectedRowData: undefined,

      scrollingOffSet: undefined,
      localCopyItems: [],
      headerFormHeight: 0,
      headerDxFormHeight: "auto",
      allBatchData: [],
      almondcoQCData: undefined,

      SelectedCustomerData: undefined,
      focusedCellData: {},
      salesUMsLocalDictionary: {},
      allowPriceChangeTriggerConfirmation: false,
      selectedProducts: [],
      taxCodeDataSourceItems: [],

      customerDataSourceItems: new ArrayStore({
        key: "ID",
        data: [],
      }),
      reviewDataList: new ArrayStore({
        key: "ID",
        data: [],
      }),
      productDataSourceItems: new ArrayStore({
        key: "ID",
        data: [],
      }),
      glListDataSourceItems: new ArrayStore({
        key: "ID",
        data: [],
      }),

      DateIn: {
        min: undefined,
        max: undefined,
      },
    };
  },
  beforeDestroy() {
    this.localDataSource.clear();
    CommonStorage.clear("sales-order-details");
    CommonStorage.clear("sales-order-details-scroll-offset");
  },
  async created() {
    this.$store.dispatch("defaultUserSettings/setDefaultCompanyBranchDivision");

    this.$store.dispatch("ui/setVisibleCommentLabels", false);
    this.showLoading(true);
    await this.initialized();
    if (this.baseView.mode == "edit") this.model.Status = "Edit";
  },
  async mounted() {},

  computed: {
    ...mapGetters({ commentPopUpItems: "ui/getCommentPopUpItems" }),
    ...mapGetters({ gridHeight: "ui/getHeightForDataGrid" }),
    getHeaderFormItems() {
      return [
        {
          itemType: "group",
          caption: "",
          colCount: 2,
          items: [
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  dataField: "DateIn",
                  label: { text: "Date" },
                  editorType: "dxDateBox",
                  required: true,
                  editorOptions: {
                    tabIndex: 1,
                    disabled: true,
                    displayFormat: "dd MMM yyyy",
                    onValueChanged: () => {
                      if (this.baseView.mode == "create") {
                        this.updateDates();
                      }
                    },
                  },
                  visible: this.baseView.mode != "create",
                },
                {
                  dataField: "DateDue",
                  label: { text: "Due Date" },
                  editorType: "dxDateBox",
                  editorOptions: {
                    tabIndex: 2,
                    disabled: true,
                    displayFormat: "dd MMM yyyy",
                  },
                  visible: this.baseView.mode != "create",
                },

                // View change for create mode
                {
                  itemType: "group",
                  visible: this.baseView.mode == "create",
                  caption: "",
                  colCount: 2,
                  items: [
                    {
                      dataField: "DateIn",
                      label: { text: "Date" },
                      editorType: "dxDateBox",
                      cssClass: "DateIn-field",
                      required: true,
                      editorOptions: {
                        tabIndex: 3,
                        disabled: true,
                        displayFormat: "dd MMM yyyy",
                        onValueChanged: () => {
                          if (this.baseView.mode == "create") {
                            this.updateDates();
                          }
                        },
                        min: this.DateIn.min,
                        max: this.DateIn.max,
                      },
                    },
                    {
                      dataField: "DateDue",
                      label: { text: "Due Date" },
                      editorType: "dxDateBox",
                      editorOptions: {
                        tabIndex: 4,
                        disabled: true,
                        displayFormat: "dd MMM yyyy",
                      },
                    },
                  ],
                },

                {
                  dataField: "ConsignmentNo",
                  visible: this.baseView.mode == "create",
                  label: {
                    text: "Consignment",
                  },
                  editorType: "dxTextBox",
                  editorOptions: {
                    tabIndex: 5,
                    maxLength: 100,
                  },
                },

                {
                  dataField: "OrderNo",
                  label: { text: "Cust PO" },
                  editorType: "dxTextBox",
                  editorOptions: {
                    tabIndex: 6,
                    disabled: true,
                    onFocusIn: (e) => {
                      e.event.target.select();
                    },
                    onValueChanged: this.onOrderNoChanged.bind(this),
                  },
                },
                {
                  itemType: "group",
                  caption: "",
                  colCount: 2,
                  cssClass: "gc-invoice-agent",
                  items: [
                    {
                      dataField: "AgentName",
                      cssClass: "agent-name",
                      label: { text: "Agent" },
                      editorType: "dxTextBox",
                      visible: FeatureStorage.checkPermission(
                        "AgentCommission",
                        this.baseView.mode
                      ),
                      editorOptions: {
                        disabled: true,
                        tabIndex: -1,
                        onFocusIn: (e) => {
                          e.event.target.select();
                        },
                        readOnly: true,
                      },
                    },
                    {
                      dataField: "AgentCommissionPcnt",
                      label: { text: "Commission" },
                      editorType: "dxNumberBox",
                      visible: FeatureStorage.checkPermission(
                        "AgentCommission",
                        this.baseView.mode
                      ),
                      editorOptions: {
                        tabIndex: 7,
                        disabled: true,
                        format: "Decimal-3-2P",
                        width: 80,
                        onValueChanged: (e) => {
                          this.onAgentCommissionPcntValueChanged(e);
                        },
                        onFocusIn: (e) => {
                          e.event.target.select();
                        },
                      },
                    },
                  ],
                },
                {
                  itemType: "group",
                  caption: "",
                  cssClass: "deliver-to",
                  items: [
                    {
                      screenField: "DeliverTo",
                      dataField: "CustomerDeliveryAddressLineNo",
                      label: {
                        text: "Deliver To",
                      },
                      valueExpr: "LineNo",
                      ref: "DeliveryAddressCompanyName",
                      required: true,
                      filter: [["CustomerCode", "=", this.model.CustomerCode]],
                      editorOptions: {
                        tabIndex: 8,
                        onItemClick: this.onDeliverToRowClick,
                        select: [
                          "LineNo",
                          "CompanyName",
                          "CustomerCode",
                          "PhysicalAddressCountry",
                          "PhysicalAddressPostcode",
                          "PhysicalAddressState",
                          "PhysicalAddressStreetLine1",
                          "PhysicalAddressStreetLine2",
                          "PhysicalAddressSuburb",
                          "RegionCode",
                          "SubregionCode",
                        ],
                      },
                      visible: this.baseView.mode != "review",
                    },
                    {
                      screenField:
                        this.baseView.mode != "review"
                          ? "DeliverTo"
                          : undefined,
                      dataField: "DeliveryAddressCompanyName",
                      ref: "DeliveryAddressCompanyName",
                      label: {
                        text: "Deliver To",
                      },
                      required: true,
                      visible: this.baseView.mode == "review",
                    },
                    {
                      screenField: "DeliveryCompanyView",
                      editAddress:
                        this.baseView.mode != "review" ? true : false,
                    },
                  ],
                },
              ],
            },
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  screenField:
                    this.baseView.mode == "create" ? "Branch" : undefined,
                  label: { text: "Branch" },
                  dataField:
                    this.baseView.mode == "create"
                      ? "BranchCode"
                      : "BranchDescription",
                  required: true,
                  editorOptions: {
                    tabIndex: this.baseView.mode == "create" ? 9 : -1,
                    readOnly: this.baseView.mode != "create",
                    showClearButton: true,
                    onValueChanged: () => {
                      this.triggerPriceChange("Branch");
                    },
                  },
                  displayExpr: (item) => {
                    if (item) return item.Code + " - " + item.Description;
                    else return "";
                  },
                  searchExpr: ["Code", "Description"],
                  itemTemplate: (data) => {
                    return ` <div class="custom-auto-complete" id="${data.Code}" >
                              <span class="auto-complete-primary">
                                ${data.Description}
                              </span>
                              <span class="record-secondary gc-grid-code">
                                ${data.Code}
                              </span> 
                            </div>`;
                  },
                  
                },
                {
                  screenField:
                    this.baseView.mode == "create" ? "Division" : undefined,
                  label: { text: "Division" },
                  dataField:
                    this.baseView.mode == "create"
                      ? "DivisionCode"
                      : "DivisionDescription",
                  required: true,
                  editorOptions: {
                    tabIndex: this.baseView.mode == "create" ? 10 : -1,
                    readOnly: this.baseView.mode != "create",
                    showClearButton: true,
                  },
                  displayExpr: (item) => {
                    if (item) return item.Code + " - " + item.Description;
                    else return "";
                  },
                  searchExpr: ["Code", "Description"],
                  itemTemplate: (data) => {
                    return ` <div class="custom-auto-complete" id="${data.Code}" >
                              <span class="auto-complete-primary">
                                ${data.Description}
                              </span>
                              <span class="record-secondary gc-grid-code">
                                ${data.Code}
                              </span> 
                            </div>`;
                  },
                },
                {
                  screenField: "OrderType",
                  label: { text: "SO Type" },
                  editorOptions: {
                    tabIndex: 11,
                    showClearButton: true,
                    onItemClick: this.onSalesOrderTypeRowClick,
                    select: ["Code", "Description", "Inactive", "Status"],
                    onValueChanged: () => {
                      this.triggerPriceChange("SO Type");
                    },
                  },
                  required: true,
                  displayExpr: (item) => {
                    if (item) return item.Code + " - " + item.Description;
                    else return "";
                  },
                  searchExpr: ["Code", "Description"],
                  itemTemplate: (data) => {
                    return ` <div class="custom-auto-complete" id="${data.Code}" >
                              <span class="auto-complete-primary">
                                ${data.Description}
                              </span>
                              <span class="record-secondary gc-grid-code">
                                ${data.Code}
                              </span> 
                            </div>`;
                  },
                },
                {
                  screenField: "SalesRepresentative",
                  label: { text: "Sales Rep" },
                  dataField: "SalesRepresentativeCode",
                  required: true,
                  editorOptions: {
                    tabIndex: 12,
                    showClearButton: true,
                    select: [
                      "Code",
                      "Inactive",
                      "FirstName",
                      "Surname",
                      "FullName",
                    ],
                    onKeyDown: (e) => {
                      if (!this.exchangeRateEditable) this.moveOnKeyDown(e);
                    },
                  },
                  displayExpr: (item) => {
                    if (item) return item.Code + " - " + item.FullName;
                    else return "";
                  },
                  searchExpr: ["Code",  "FirstName", "Surname", "FullName"],
                  itemTemplate: (data) => {
                    return ` <div class="custom-auto-complete" id="${data.Code}" >
                              <span class="auto-complete-primary">
                                ${data.FullName}
                              </span>
                              <span class="record-secondary gc-grid-code">
                                ${data.Code}
                              </span> 
                            </div>`;
                  },
                },
                {
                  itemType: "group",
                  caption: "",
                  colCount: 2,
                  items: [
                    {
                      screenField: "ExchangeRate",
                      dataField: "ExchangeRate",
                      label: { text: "Exchange Rate" },
                      cssClass: "exchange-rate",
                      editorType: "dxNumberBox",
                      editorOptions: {
                        tabIndex: !this.exchangeRateEditable ? -1 : undefined,
                        readOnly: !this.exchangeRateEditable,
                        onValueChanged: this.onExchangeValueChanged,
                        onKeyDown: (e) => {
                          this.moveOnKeyDown(e);
                        },
                      },
                    },
                    {
                      dataField: "CurrencyCode",
                      label: {
                        text: "Currency",
                        visible: false,
                      },
                      editorOptions: {
                        tabIndex: -1,
                        readOnly: true,
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ];
    },
    exchangeRateEditable() {
      return (
        this.baseView.mode != "review" &&
        this.model.CompanyCurrency != this.model.CurrencyCode
      );
    },
    getHeaderToolbar() {
      return {
        audit: this.baseView.mode == "review",
        status: true,
        notes: false,
        save: true,
        saveKey: true,
        screenName: "Invoice Review",
        pdf: this.baseView.mode == "review",
        auditItems: [
          {
            itemType: "group",
            caption: "",
            items: [
              {
                dataField: "FormattedDateCreated",
                label: { text: "Created By", location: "left" },
                editorType: "dxTextBox",
                editorOptions: {},
              },
              {
                dataField: "FormattedDateChanged",
                label: { text: "Changed By", location: "left" },
                editorType: "dxTextBox",
                editorOptions: {},
              },
            ],
          },
        ],
        customStatus: {
          enabled: true,
          key: "CustomStatus",
        },
        toolbarFormItems: [
          {
            screenField: "Customer",
            dataField: "CustomerCode",
            cssClass: "toolbar-customer-label",
            editorOptions: {
              tabIndex: 1,
              dropDownOptions: {
                minWidth: 300,
                elementAttr: {
                  id: "in-v2-customer-details",
                },
              },
              width: 300,
              placeholder: "Customer Name",
              showClearButton: true,
              // onItemClick: this.onCustomerRowClick,
              onSelectionChanged: (e) => {
                this.onCustomerValueSelected(e);
              },
              elementAttr: {
                id: "so-v4-customer-name",
              },
              readOnly: !this.IsCustomerEditable,
              select: [
                "Code",
                "CurrencyCode",
                "Name",
                "StopCredit",
                "TermCalculation",
                "Terms",
                "SalesRepresentativeCode",
                "Gst",
              ],
            },
            label: {
              text: "Customer",
              visible: false,
            },
            required: true,
            visible: this.baseView.mode == "create",
            displayExpr: (item) => {
              if (item) return item.Code + " - " + item.Name;
              else return "";
            },
            searchExpr: ["Code", "Name"],
            itemTemplate: (data) => {
              return ` <div class="custom-auto-complete" id="${data.Code}" >
                        <span class="auto-complete-primary">
                          ${data.Name}
                        </span>
                        <span class="record-secondary gc-grid-code">
                          ${data.Code}
                        </span> 
                      </div>`;
            },
          },
        ],
        formContentReady: this.toolBarFormContentReady,
      };
    },
    backButtonRoute() {
      if (this.baseView.query && this.baseView.query.from)
        return this.baseView.query.from;
      else return "/invoiced-order";
    },
    getDeliverToFormItems() {
      return [
        {
          itemType: "group",
          caption: "",
          colCount: 1,
          items: [
            {
              dataField: "DeliveryAddressCompanyName",
              editorType: "dxTextBox",
              label: {
                text: "Name",
              },
              editorOptions: {
                maxLength: "100",
                readOnly: true,
              },
            },
            {
              screenField: "PhysicalAddressStreetLine1",
              dataField: "DeliveryAddressStreetLine1",
              editorOptions: {
                maxLength: "50",
              },
            },
            {
              screenField: "PhysicalAddressStreetLine2",
              dataField: "DeliveryAddressStreetLine2",
              editorOptions: {
                maxLength: "50",
              },
            },
            {
              screenField: "PhysicalAddressSuburb",
              dataField: "DeliveryAddressSuburb",
              editorOptions: {
                maxLength: "50",
              },
            },
            {
              screenField: "PhysicalAddressState",
              dataField: "DeliveryAddressState",
              editorOptions: {
                maxLength: "50",
              },
            },
            {
              screenField: "PhysicalAddressPostcode",
              dataField: "DeliveryAddressPostcode",
              editorOptions: {
                maxLength: "50",
              },
            },
            {
              screenField: "PhysicalAddressCountry",
              dataField: "DeliveryAddressCountry",
              editorOptions: {
                maxLength: "50",
              },
            },
          ],
        },
      ];
    },
    deliveryAddressPopupConfig() {
      return {
        save: {
          text: "Save",
          disabled: !this.deliveryAddressFormIsDirty,
          clickCallback: async () => {
            await this.updateDeliveryAddress();
          },
        },
        back: {
          text: "Cancel",
          clickCallback: async () => {
            this.closeDeliveryAddressPopup();
          },
        },
      };
    },
    deliveryAddressFormIsDirty() {
      if (!this.DeliveryAddressModel) return false;

      return (
        this.DeliveryAddressModel.DeliveryAddressCompanyName !==
          this.model.DeliveryAddressCompanyName ||
        this.DeliveryAddressModel.DeliveryAddressStreetLine1 !==
          this.model.DeliveryAddressStreetLine1 ||
        this.DeliveryAddressModel.DeliveryAddressStreetLine2 !==
          this.model.DeliveryAddressStreetLine2 ||
        this.DeliveryAddressModel.DeliveryAddressSuburb !==
          this.model.DeliveryAddressSuburb ||
        this.DeliveryAddressModel.DeliveryAddressState !==
          this.model.DeliveryAddressState ||
        this.DeliveryAddressModel.DeliveryAddressPostcode !==
          this.model.DeliveryAddressPostcode ||
        this.DeliveryAddressModel.DeliveryAddressCountry !==
          this.model.DeliveryAddressCountry
      );
    },

    // Details Tab
    getGridColumns() {
      return this.baseView.mode == "create"
        ? this.getCreateColumns
        : this.getColumns;
    },
    getColumns() {
      return this.parseGridColumns([
        {
          screenField: "Product",
          label: "Product / Account",
          allowEditing: true,
          minWidth: ColumnWidth.ProductOrAccount.minWidth,
          dataField: "ItemCode",
          filterDataField: "ItemCode",
          gridDataField: "ItemCode",
          filterIndex: 0,
          cellTemplateRecord: {
            primary: [
              {
                dataField: "ItemDescription",
              },
            ],
            secondary: [
              {
                dataField: "ItemCode",
              },
            ],
          },
          exportFields: [
            {
              dataField: "ItemCode",
              label: "Item",
            },
            {
              dataField: "ItemDescription",
              label: "Item Description",
            },
          ],
          editorType: "dxSelectBox",
          cssClass:
            this.baseView.mode != "review"
              ? "gc-editable-cell-color"
              : undefined,
          editorOptions: {
            dataSource: ProductService.dataSourceConfig(
              "api/ProductFilterGroupSaleItemCompanyBranch",
              {
                filter: [
                  ["ItemType", "=", "S"],
                  ["IsDefault", "=", "Y"],
                  ["AvailableForSale", "=", "Y"],
                  ["CustomerCode", "=", this.model.CustomerCode],
                  ["Inactive", "=", "N"],
                  ["BranchCode", "=", this.model.BranchCode],
                ],
                sort: [{ selector: "Description", asc: true }],
                pageSize: 200,
              }
            ),
            displayExpr: "Description",
            valueExpr: "ItemCode",
            searchExpr: ["ItemCode", "Description"],
            searchMode: "contains",
            showClearButton: true,
            searchEnabled: true,
            dropDownOptions: {
              minWidth: 500,
            },
            itemTemplate: (data) => {
              return ` <div class="custom-auto-complete" id="${data.ItemCode}" >
                <span class="auto-complete-primary">
                  ${data.Description}
                </span>
                <span class="record-secondary gc-grid-code">
                  ${data.ItemCode}  
                </span> 
              </div>`;
            },
            onItemClick: (e, cellInfo) => {
              var dataGrid = this.$refs["dx-data-grid-comp"].instance;
              dataGrid.collapseRow(cellInfo.data.LineNo);
              var itemData = e.itemData;
              var temp = {
                SalesUM: itemData.SalesUM,
                StockUM: itemData.StockUM,
                ItemDescription: itemData.Description,
                ItemCode: itemData.ItemCode,
                ItemType: itemData.ItemType,
                SalesUMOrderQty: 0,
                IsBatchProduct: itemData.IsBatchProduct,
                InvoiceProductBatches: [],
              };

              if (
                FeatureStorage.checkPermission(
                  "AlmondcoDespatchData",
                  "create"
                ) &&
                this.almondcoQCData
              ) {
                temp.IsBatchProduct = "Yes";
                temp.DespatchData = false;
              }

              this.localDataSource
                .update(cellInfo.data.LineNo, temp)
                .then((data) => {
                  if (data.IsBatchProduct != "No") {
                    data.SalesUMQty = 0;
                    if (
                      FeatureStorage.checkPermission(
                        "AlmondcoDespatchData",
                        "create"
                      ) &&
                      this.almondcoQCData
                    ) {
                      data.SalesUMQty = data.OriginalSalesUMQty;
                      data.InvoiceProductBatches.push({
                        CartonOutstandingQty: 0,
                        CartonQty: 0,
                        CartonWeight: 0,
                        ChangedBy: undefined,
                        Code: undefined,
                        CreatedBy: undefined,
                        DateChanged: undefined,
                        DateCreated: undefined,
                        DespatchDate: new Date(),
                        InvoiceLineNo: data.LineNo,
                        LotNo: undefined,
                        PackedOnDate: undefined,
                        PalletNo: undefined,
                        RecordID: undefined,
                        TotalWeight: 0,
                        WorkOrder: undefined,
                        PackSize: data.PackSize,
                        SalesUMConversion: data.SalesUMConversion,
                      });
                    } else {
                      data.InvoiceProductBatches.push({
                        BatchNo: undefined,
                        ItemCode: data.ItemCode,
                        StockUMBatchQty: 0,
                        SalesUMBatchQty: 0,
                        SalesUMInvoiceConversion: 0,
                        SupplierCode: undefined,
                        SupplierName: undefined,
                        InvoiceLineNo: data.LineNo,
                        SalesUMCreditedQty: 0,
                        UseByDate: undefined,
                        BatchLineNo: 0,
                        AvailableQty: 0,
                        AllocatedQty: 0,
                      });
                    }
                  } else {
                    data.SalesUMQty = data.OriginalSalesUMQty;
                  }
                  SalesOrderV3Service.setSaleItemPrice(
                    data,
                    data,
                    this.model.SalesOrderTypeCode,
                    this.model.BranchCode,
                    this.model.CustomerCode,
                    this.model.ExchangeRate,
                    this.model.DateIn,
                    this.model.RequiredByDate,
                    false
                  ).then((res) => {
                    var newData = { ...data, ...res };

                    InvoiceService.getCustomDataSocurce(
                      "ProductUnitOfMeasure",
                      {
                        filter: `ProductCode eq '${newData.ItemCode}' and BranchCode eq '${this.model.BranchCode}'`,
                        select: [
                          "ProductCode",
                          "BranchCode",
                          "UnitOfMeasureCode",
                        ],
                      }
                    ).then((ums) => {
                      newData.SalesUMs = ums.data.value;
                      this.localDataSource
                        .update(cellInfo.data.LineNo, newData)
                        .then((x) => {
                          dataGrid.saveEditData();
                          this.setFooterItems(this.localDataSource._array);

                          var rowIndex = dataGrid.getRowIndexByKey(x.LineNo);
                          dataGrid.repaintRows([rowIndex]);

                          setTimeout(() => {
                            dataGrid.refresh();

                            if (
                              x.IsBatchProduct != "No" &&
                              !this.almondcoQCData
                            )
                              this.getBatchItems([x.ItemCode]);
                          }, 10);
                        });
                    });
                  });

                  e.component.blur();
                });
            },
            onFocusIn: (e) => {
              this.removeCellFocus();

              e.event.target.select();
              e.element.parentElement.classList.add("key-pad-focused-cell");
            },
            onKeyDown: (e, cellInfo) => {
              if (e.event.keyCode == 9 && e.event.shiftKey) {
                if (cellInfo.rowIndex == 0) {
                  e.component.blur();
                  if (cellInfo.rowIndex == 0) {
                    setTimeout(() => {
                      e.event.preventDefault();
                    }, 1);
                  }
                  setTimeout(() => {
                    var rowIndex = cellInfo.rowIndex;

                    if (rowIndex == 0) {
                      var headerFormInstance =
                        this.$refs["gc-header-form"].$refs["gc-form-comp"]
                          .instance;

                      if (this.exchangeRateEditable) {
                        headerFormInstance
                          .getEditor("ExchangeRate")
                          .instance()
                          .focus();
                      } else {
                        headerFormInstance
                          .getEditor("SalesRepresentativeCode")
                          .instance()
                          .focus();
                      }
                    }
                  }, 2);
                }
              }
            },
          },
        },
        {
          screenField: "TaxCode",
          label: "Tax",
          allowEditing: false,
          width: ColumnWidth.TaxCode.width,
          minWidth: ColumnWidth.TaxCode.minWidth,
          cellTemplate: undefined,
        },
        {
          screenField: "SalesUM",
          allowEditing: true,
          width: ColumnWidth.UnitOfMeasure.width,
          minWidth: ColumnWidth.UnitOfMeasure.minWidth,
          cssClass:
            this.baseView.mode != "review"
              ? "gc-editable-cell-color"
              : undefined,
          calculateDisplayValue: (e) => {
            return e.SalesUM;
          },
          editorOptions: {
            onFocusIn: (e, cellInfo) => {
              this.removeCellFocus();

              if (cellInfo.data.SalesUMs.length > 1) {
                e.event.target.select();
                e.element.parentElement.classList.add("key-pad-focused-cell");
              }
            },
            showClearButton: false,
            searchEnabled: true,
            dropDownOptions: {
              minWidth: 500,
            },
          },
          lookup: {
            dataSource: (data) => {
              if (Object.keys(data).length != 0 && data && data.data) {
                return data.data.SalesUMs;
              } else return [];
            },
            displayExpr: "UnitOfMeasureCode",
            valueExpr: "UnitOfMeasureCode",
          },
          setCellValue: async (newData, value, currentRowData) => {
            newData.SalesUM = value;
            newData.SalesUMQty = currentRowData.SalesUMQty;

            this.updatePriceLookup(newData, value, currentRowData);
          },
        },
        // Ordered Qty
        {
          screenField: "SalesUMOrderQty",
          gridDataField: "SalesUMOrderQty",
          allowEditing: false,
          width: ColumnWidth.Price4DecimalPoints.width,
          maxWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          format: "Decimal-16-4",
        },
        // Original Qty
        {
          screenField: "SalesUMOrderQty",
          gridDataField: "OriginalSalesUMQty",
          label: "Original Qty",
          allowEditing: false,
          width: ColumnWidth.Price4DecimalPoints.width,
          maxWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          format: "Decimal-16-4",
        },
        {
          screenField: "SalesUMInvoicedQty",
          label: "New Qty",
          gridDataField: "SalesUMQty",
          allowEditing: true,
          width: ColumnWidth.Price4DecimalPoints.width,
          maxWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          format: "Decimal-16-4",
          editorOptions: {
            format: "###,##0.0000",
            onFocusIn: (e, cellInfo) => {
              this.removeCellFocus();

              if (cellInfo.data.IsBatchProduct == "No") {
                e.event.target.select();
                e.element.parentElement.classList.add("key-pad-focused-cell");
              }
            },
          },
          cssClass:
            this.baseView.mode != "review"
              ? "gc-editable-cell-color"
              : undefined,
          setCellValue: async (newData, value, currentRowData) => {
            newData.DiscPcnt = 0;
            newData.SalesUMQty = value;

            if (currentRowData.SalesUMFXPrice > 0) {
              var dataGrid = this.$refs["dx-data-grid-comp"].instance;
              dataGrid.saveEditData();

              newData = { ...currentRowData, ...newData };

              newData = SalesOrderState.setStockPrices(newData);
              newData = SalesOrderState.setLineTotals(newData, this.model);

              this.localDataSource
                .update(currentRowData.LineNo, newData)
                .then(() => {
                  dataGrid.saveEditData();
                  dataGrid.refresh(true).then(() => {
                    this.setFooterItems(this.localDataSource._array);
                  });
                });
            } else this.updatePriceLookup(newData, value, currentRowData);
          },
        },
        {
          screenField: "DiscountPercent",
          allowEditing: false,
          width: ColumnWidth.Discount.width,
          maxWidth: ColumnWidth.Discount.minWidth,
        },
        {
          screenField: "SalesUMFXPrice",
          allowEditing: true,
          width: ColumnWidth.Price4DecimalPoints.width,
          maxWidth: ColumnWidth.Price4DecimalPoints.minWidth,
          cssClass:
            this.baseView.mode != "review"
              ? "gc-editable-cell-color"
              : undefined,
          editorType: "dxNumberBox",
          editorOptions: {
            onFocusIn: (e) => {
              this.removeCellFocus();

              e.event.target.select();
              e.element.parentElement.classList.add("key-pad-focused-cell");
            },
          },
          setCellValue: async (newData, value, currentRowData) => {
            var dataGrid = this.$refs["dx-data-grid-comp"].instance;

            newData = await this.onPriceUpdateValidateGPToolPrcnt(
              newData,
              value,
              currentRowData
            );

            this.localDataSource
              .update(currentRowData.LineNo, newData)
              .then(() => {
                this.setFooterItems(this.localDataSource._array);
                var rowIndex = dataGrid.getRowIndexByKey(currentRowData.LineNo);
                dataGrid.repaintRows([rowIndex]);
                dataGrid.refresh();
              });

            return newData;
          },
        },
        {
          screenField: "SalesUMNetFXPrice",
          allowEditing: false,
          width: ColumnWidth.Price4DecimalPoints.width,
          minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
        },
        {
          screenField: "TaxableFXLineTotal",
          allowEditing: false,
          width: ColumnWidth.Price4DecimalPoints.width,
          minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
        },
        {
          screenField: "TaxFXTotal",
          dataField: "TaxFXLineTotal",
          allowEditing: false,
          width: ColumnWidth.Price4DecimalPoints.width,
          minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
        },
        {
          screenField: "IncTaxFXTotal",
          dataField: "FXLineTotal",
          label: "Inc Tax (Customer Currency)",
          allowEditing: false,
          width: ColumnWidth.Price4DecimalPoints.width,
          minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
        },
        {
          screenField: "GridRecordEllipsisMenu",
          allowSearch: false,
          showInColumnChooser: false,
          visible: false,
          allowEditing: false,
        },
        {
          screenField: "Comments",
          dataField: "Comment",
          allowEditing: true,
          minWidth: ColumnWidth.Comment.minWidth,
          width: ColumnWidth.Comment.width,
          cssClass:
            this.baseView.mode != "review"
              ? "gc-editable-cell-color"
              : undefined,
          editorType: "dxTextArea",
          setCellValue: async (newData, value, currentRowData) => {
            newData.Comment = value;

            this.localDataSource.update(currentRowData.LineNo, newData);
            return newData;
          },
        },
        {
          dataField: "LineNo",
          showInColumnChooser: false,
          visible: false,
          sortIndex: 0,
          sortOrder: "asc",
        },
      ]);
    },
    getDataSource() {
      if (this.baseView.mode != "create") {
        return this.localDataSource;
      } else {
        if (this.soDetailsCurrentView == "CustomerList") {
          return this.productListPostProcess(
            this.customerDataSourceItems._array
          );
        } else if (this.soDetailsCurrentView == "ProductList") {
          return this.productListPostProcess(
            this.customerDataSourceItems._array
              .filter((x) => x.SalesUMQty > 0)
              .concat(this.productDataSourceItems._array)
          );
        } else if (this.soDetailsCurrentView == "GLList") {
          return this.productListPostProcess(this.glListDataSourceItems._array);
        } else {
          return this.reviewDataList._array;
        }
      }
    },
    getGridHeight() {
      return this.gridHeight - 20 - 40 - 55 - this.headerFormHeight;
    },
    getPersistenceKey() {
      if (this.soDetailsCurrentView == "Review") {
        return (
          "sales-order-" +
          this.soDetailsCurrentView +
          "_" +
          appConfig.VUE_APP_COOKIE_NAME
        );
      } else {
        return "sales-order-details_" + appConfig.VUE_APP_COOKIE_NAME;
      }
    },
    // Child Grid
    getChildGridColumns() {
      return this.parseGridColumns(
        FeatureStorage.checkPermission("AlmondcoDespatchData", "create") &&
          this.almondcoQCData
          ? this.getAlmondcoDespatchDataColumns
          : this.getBatchDataColumns
      );
    },
    getBatchDataColumns() {
      return [
        {
          screenField: "Batch",
          gridDataField: "BatchNo",
          allowEditing: true,
          calculateDisplayValue: (e) => {
            return e.BatchNo;
          },
          lookup: {
            dataSource: (e) => {
              var items = [];
              if (Object.keys(e).length != 0 && e.data) {
                items = this.allBatchData.filter(
                  (x) =>
                    x.ItemCode == e.data.ItemCode &&
                    x.BranchCode == this.model.BranchCode
                );
              }

              return items;
            },
            displayExpr: "BatchNo",
            valueExpr: "BatchNo",
          },
          cssClass:
            this.baseView.mode != "review"
              ? "gc-editable-cell-color"
              : undefined,
          editorOptions: {
            itemTemplate: (data) => {
              return ` <div class="custom-auto-complete" id="${data.BatchNo}" >
                <span class="auto-complete-primary">
                  ${data.BatchNo}
                </span> <br />
                <span class="record-secondary gc-grid-code">
                  ${data.SupplierCode} 
                  ${data.SupplierName}
                  ${data.UseByDate ? data.UseByDate : ""}
                  ${data.AvailableQty}
                </span> 
              </div>`;
            },
            onItemClick: (e, cellInfo) => {
              var itemData = e.itemData;
              var batchItems = cellInfo.parentLine.data.InvoiceProductBatches;

              var data = batchItems.find(
                (x) => x.BatchLineNo == cellInfo.data.BatchLineNo
              );

              var usedBatchQty = 0;
              // this.reviewDataList._array.forEach(element => {
              //   if(element.ItemCode == itemData.ItemCode && element.IsBatchProduct != "No"){
              //     element.InvoiceProductBatches.forEach(batch => {
              //       // usedBatchQty = usedBatchQty + batch.SalesUMBatchQty;
              //     });

              //   }
              // });

              data.BatchNo = itemData.BatchNo;
              data.ItemCode = itemData.ItemCode;
              data.StockUMBatchQty = 0;
              data.SupplierCode = itemData.SupplierCode;
              data.SupplierName = itemData.SupplierName;
              data.UseByDate = itemData.UseByDate;
              data.AllocatedQty = itemData.AllocatedQty;
              data.AvailableQty = itemData.AvailableQty - usedBatchQty;

              if (this.soDetailsCurrentView == "CustomerList")
                data.ParentID = itemData.ParentID;

              // cellInfo.parentLine.data.SalesUMQty =
              //   cellInfo.parentLine.data.SalesUMQty -
              //   data.SalesUMBatchQty +
              //   itemData.AllocatedQty;

              // data.SalesUMBatchQty = itemData.AllocatedQty;

              var dataGrid = this.$refs["dx-data-grid-comp"].instance;

              // this.updatePriceData(
              //   cellInfo.parentLine.data,
              //   cellInfo.parentLine.data
              // );
              dataGrid.saveEditData();
              this.setFooterItems(this.localDataSource._array);
              setTimeout(() => {
                dataGrid.refresh(true);
              }, 10);
            },
            onOpened: (e, parentRow) => {
              this.removeParentGridCellFocus();
              e.component.beginUpdate();

              var items = e.component.getDataSource().items();
              items.forEach((item) => {
                item.disabled = false;

                e.component.option("items", items);
              });

              parentRow.data.InvoiceProductBatches.forEach((element) => {
                items.forEach((item, index) => {
                  if (element.BatchNo == item.BatchNo) {
                    items[index].disabled = true;
                    e.component.option("items", items);
                  }
                });
              });
              e.component.endUpdate();
            },
          },
        },
        {
          screenField: "Supplier",
          allowEditing: false,
          gridDataField: "SupplierCode",
          cellTemplateRecord: {
            primary: [
              {
                dataField: "SupplierName",
              },
            ],
            secondary: [
              {
                dataField: "SupplierCode",
              },
            ],
          },
        },
        {
          screenField: "UseByDate",
          allowEditing: false,
        },
        // Available Quantity
        {
          screenField: "Quantity",
          dataField: "AvailableQty",
          label: "Available",
        },
        // Invoiced Quantity
        {
          screenField: "SalesUMInvoicedQty",
          gridDataField: "SalesUMBatchQty",
          allowEditing: true,
          cssClass:
            this.baseView.mode != "review"
              ? "gc-editable-cell-color"
              : undefined,
          editorType: "dxNumberBox",
          format: "Decimal-16-4",
          editorOptions: {
          format: "Decimal-16-4",
            onFocusIn: (e) => {
              this.removeCellFocus();

              e.event.target.select();
              e.element.parentElement.classList.add("key-pad-focused-cell");

              this.removeParentGridCellFocus();
            },
          },
          setCellValue: async (newData, value, currentRowData) => {
            var dataGrid = this.$refs["dx-data-grid-comp"].instance;
            var parentLine = undefined;

            var availableQty = currentRowData.AvailableQty;

            var items = dataGrid
              .getDataSource()
              .items()
              .filter((x) => x.ItemCode == currentRowData.ItemCode);
            items.forEach((element) => {
              if (element.IsBatchProduct.toLowerCase() != "no") {
                element.InvoiceProductBatches.forEach((item) => {
                  if (
                    item.BatchNo == currentRowData.BatchNo &&
                    element.ID != currentRowData.ParentID
                  ) {
                    availableQty = availableQty - item.SalesUMBatchQty;
                  }
                });
              }
            });

            if (this.baseView.mode != "create") {
              await dataGrid.byKey(currentRowData.InvoiceLineNo).then((res) => {
                parentLine = res;
              });

              if (availableQty < value && parentLine) {
                await alert(
                  `${parentLine.ItemDescription} only has ${availableQty} left to pick.  Please enter in a invoiced quantity that is ${availableQty} or less.`,
                  "Validation Error"
                );

                newData.SalesUMBatchQty = availableQty;
              } else {
                newData.SalesUMBatchQty = value;
              }
              parentLine.SalesUMQty =
                parentLine.SalesUMQty -
                currentRowData.SalesUMBatchQty +
                newData.SalesUMBatchQty;

              this.localDataSource
                .update(parentLine.LineNo, {
                  SalesUMQty: parentLine.SalesUMQty,
                })
                .then((res) => {
                  res = SalesOrderState.setStockPrices(res);
                  SalesOrderState.setLineTotals(res, this.model);
                  dataGrid.refresh(true).then(() => {
                    this.setFooterItems(this.localDataSource._array);
                  });
                });
            } else {
              await dataGrid.byKey(currentRowData.ParentID).then((res) => {
                parentLine = res;
              });

              if (availableQty < value && parentLine) {
                await alert(
                  `${parentLine.Description} only has ${availableQty} left to pick.  Please enter in a invoiced quantity that is ${availableQty} or less.`,
                  "Validation Error"
                );

                newData.SalesUMBatchQty = availableQty;
              } else {
                newData.SalesUMBatchQty = value;
              }

              parentLine.SalesUMQty =
                parentLine.SalesUMQty -
                currentRowData.SalesUMBatchQty +
                newData.SalesUMBatchQty;

              var batchIndex = parentLine.InvoiceProductBatches.findIndex(
                (x) => x.BatchNo == currentRowData.BatchNo
              );
              if (batchIndex != -1) {
                parentLine.InvoiceProductBatches[batchIndex].SalesUMBatchQty =
                  newData.SalesUMBatchQty;
                this.updateDataSource(parentLine, undefined, true);
              }

              this.updatePriceData(parentLine);
            }
            return newData;
          },
        },
        {
          caption: "",
          dataField: "",
          type: "buttons",
          width: 110,
          buttons: [
            "delete",
            {
              hint: "Add Batch Line",
              icon: "plus",
              // visible: this.isEditable,
              onClick: async (e) => {
                var dataGrid = this.$refs["dx-data-grid-comp"].instance;
                var parentLine = undefined;
                if (this.baseView.mode == "create") {
                  await dataGrid.byKey(e.row.data.ParentID).then((res) => {
                    parentLine = res;
                  });
                } else {
                  await dataGrid.byKey(e.row.data.InvoiceLineNo).then((res) => {
                    parentLine = res;
                  });
                }

                var row = e.component
                  .instance()
                  .getDataSource()
                  .items()
                  .find((x) => !x.BatchNo || !x.SalesUMBatchQty);

                var validations = [];

                if (row) {
                  if (!row.BatchNo) {
                    validations.push({
                      message: "Batch No is required",
                    });
                  }
                  if (!row.SalesUMBatchQty) {
                    validations.push({
                      message: "Invoiced Quantity is required",
                    });
                  }
                }
                if (validations.length > 0)
                  this.displayValidationSummary(validations);
                else {
                  let maxLineNo = 0;

                  if (parentLine.InvoiceProductBatches.length > 0) {
                    maxLineNo = Math.max.apply(
                      Math,
                      parentLine.InvoiceProductBatches.map(function (o) {
                        return o.BatchLineNo;
                      })
                    );
                  }

                  parentLine.InvoiceProductBatches.push({
                    BatchNo: undefined,
                    ItemCode: parentLine.ItemCode,
                    StockUMBatchQty: 0,
                    SalesUMBatchQty: 0,
                    SalesUMInvoiceConversion: 0,
                    SupplierCode: undefined,
                    SupplierName: undefined,
                    // InvoiceLineNo: parentLine.LineNo,
                    SalesUMCreditedQty: 0,
                    UseByDate: undefined,
                    BatchLineNo: maxLineNo + 1,
                    AvailableQty: 0,
                    AllocatedQty: 0,
                    ParentID: parentLine.ID,
                  });

                  dataGrid.saveEditData();

                  this.setFooterItems(this.localDataSource._array);
                  setTimeout(() => {
                    dataGrid.refresh(true);
                  }, 10);
                }
              },
            },
          ],
          visible: this.baseView.mode === "create",
        },
      ];
    },
    getAlmondcoDespatchDataColumns() {
      return [
        {
          screenField: "Date",
          gridDataField: "DespatchDate",
          label: "Despatch Date",
          filterIndex: 0,
          editable: true,
          minWidth: 150,
          editorType: "dxDateBox",
          allowEditing: true,
          cssClass:
            this.baseView.mode != "review"
              ? "gc-editable-cell-color"
              : undefined,
          editorOptions: {
            onFocusIn: (e) => {
              this.removeCellFocus();

              e.event.target.select();
              e.element.parentElement.classList.add("key-pad-focused-cell");
            },
          },
          setCellValue: async (newData, value) => {
            newData.DespatchDate = value;
            return newData;
          },
        },
        {
          screenField: "Date",
          gridDataField: "PackedOnDate",
          label: "Packed On Date",
          editable: true,
          minWidth: 150,
          editorOptions: {
            max: new Date(),
            onFocusIn: (e) => {
              this.removeCellFocus();

              e.event.target.select();
              e.element.parentElement.classList.add("key-pad-focused-cell");
            },
          },
          editorType: "dxDateBox",
          allowEditing: true,
          cssClass:
            this.baseView.mode != "review"
              ? "gc-editable-cell-color"
              : undefined,
          setCellValue: async (newData, value) => {
            newData.PackedOnDate = value;
            return newData;
          },
        },
        {
          screenField: "WorkOrder",
          editorType: "dxSelectBox",
          allowEditing: true,
          cssClass:
            this.baseView.mode != "review"
              ? "gc-editable-cell-color"
              : undefined,
          makeReadOnly: (data) => {
            if (data.data.isNew) return false;
            else return true;
          },
          // validationRules: [
          //   {
          //     type: "required",
          //     message: "Work Order is required",
          //   },
          // ],
          dataType: "string",
          cellTemplate: undefined,
          editorOptions: {
            dataSource: ProductService.dataSourceConfig("api/WorklogAll", {
              select: [
                "WorkOrderNo",
                "BillNo",
                "BomDescription",
                "CustomerName",
                "WorkOrderNoComputedString",
              ],
              sort: {
                selector: "WorkOrderNo",
                asc: true,
              },
            }),
            displayExpr: "WorkOrderNoComputedString",
            valueExpr: "WorkOrderNo",
            searchExpr: ["WorkOrderNoComputedString", "WorkOrderNo"],
            searchMode: "contains",
            showClearButton: true,
            searchEnabled: true,
            dropDownOptions: {
              minWidth: 500,
            },
            itemTemplate: (data) => {
              return ` <div class="custom-auto-complete" id="${data.WorkOrderNo}" >
                <span class="auto-complete-primary">
                  ${data.WorkOrderNo}
                </span><br />
                <span class="record-secondary gc-grid-code">
                  ${data.BillNo}  <br />
                  ${data.BomDescription}   <br />
                  ${data.CustomerName}  
                </span> 
              </div>`;
            },
            onFocusIn: (e) => {
              this.removeCellFocus();

              e.event.target.select();
              e.element.parentElement.classList.add("key-pad-focused-cell");
            },
          },
          setCellValue: async (newData, value) => {
            newData.WorkOrder = value;
            return newData;
          },
        },
        {
          screenField: "SalesOrderLine",
          gridDataField: "LotNo",
          label: "Lot No",
          minWidth: 100,
          format: "Decimal-16-0",
          allowEditing: true,
          cssClass:
            this.baseView.mode != "review"
              ? "gc-editable-cell-color"
              : undefined,
          editorType: "dxNumberBox",
          editorOptions: {
            onFocusIn: (e) => {
              this.removeCellFocus();

              e.event.target.select();
              e.element.parentElement.classList.add("key-pad-focused-cell");
            },
          },
          setCellValue: async (newData, value) => {
            newData.LotNo = value;
            return newData;
          },
        },
        {
          screenField: "SalesOrderLine",
          gridDataField: "PalletNo",
          label: "Pallet No",
          minWidth: 100,
          format: "Decimal-16-0",
          allowEditing: true,
          cssClass:
            this.baseView.mode != "review"
              ? "gc-editable-cell-color"
              : undefined,
          editorType: "dxNumberBox",
          editorOptions: {
            onFocusIn: (e) => {
              this.removeCellFocus();

              e.event.target.select();
              e.element.parentElement.classList.add("key-pad-focused-cell");
            },
          },
          setCellValue: async (newData, value) => {
            newData.PalletNo = value;
            return newData;
          },
        },
        {
          screenField: "ReceivedQty",
          gridDataField: "CartonQty",
          label: "Carton Quantity",
          width: ColumnWidth.Quantity4DecimalPoints.width,
          maxWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          format: "Decimal-16-4",
          allowEditing: true,
          cssClass:
            this.baseView.mode != "review"
              ? "gc-editable-cell-color"
              : undefined,
          editorType: "dxNumberBox",
          editorOptions: {
            format: "Decimal-16-4",
            onFocusIn: (e) => {
              this.removeCellFocus();

              e.event.target.select();
              e.element.parentElement.classList.add("key-pad-focused-cell");
            },
          },
          setCellValue: async (newData, value, currentRowData) => {
            var dataGrid = this.$refs["dx-data-grid-comp"].instance;

            newData.CartonQty = value;
            newData.TotalWeight = value * currentRowData.PackSize;

            this.localDataSource
              .byKey(currentRowData.InvoiceLineNo)
              .then((res) => {
                var TotalWeight = 0;
                res.InvoiceProductBatches.forEach((element) => {
                  TotalWeight = TotalWeight + element.TotalWeight;
                });

                var obj = {
                  DespatchData: true,
                  SalesUMQty:
                    res.SalesUMQty -
                    (currentRowData.CartonQty * currentRowData.PackSize) /
                      currentRowData.SalesUMConversion +
                    newData.TotalWeight / currentRowData.SalesUMConversion,
                };

                var rowIndex = dataGrid.getRowIndexByKey(currentRowData.LineNo);
                dataGrid.cellValue(rowIndex, "SalesUMQty", obj.SalesUMQty);

                this.localDataSource
                  .update(currentRowData.InvoiceLineNo, obj)
                  .then((res) => {
                    res = SalesOrderState.setStockPrices(res);
                    SalesOrderState.setLineTotals(res, this.model);
                    dataGrid.refresh(true).then(() => {
                      this.setFooterItems(this.localDataSource._array);

                      return newData;
                    });
                  });
              });
          },
        },
        {
          screenField: "ReceivedQty",
          gridDataField: "TotalWeight",
          label: "Despatch Weight",
          minWidth: 100,
        },

        {
          caption: "",
          dataField: "",
          type: "buttons",
          width: 110,
          buttons: [
            "delete",
            {
              hint: "Add Despatch Line",
              icon: "plus",
              onClick: async (e) => {
                var dataGrid = this.$refs["dx-data-grid-comp"].instance;

                var row = e.component
                  .instance()
                  .getDataSource()
                  .items()
                  .find((x) => !x.PackedOnDate || !x.WorkOrder || !x.CartonQty);

                var validations = [];

                if (row) {
                  if (!row.PackedOnDate) {
                    validations.push({
                      message: "Packed On Date is required",
                    });
                  }
                  if (!row.WorkOrder) {
                    validations.push({
                      message: "Work Order is required",
                    });
                  }
                  if (!row.CartonQty) {
                    validations.push({
                      message: "Carton Quantity is required",
                    });
                  }
                }

                if (validations.length > 0)
                  this.displayValidationSummary(validations);
                else {
                  var parentLine = undefined;
                  await dataGrid.byKey(e.row.data.InvoiceLineNo).then((res) => {
                    parentLine = res;
                  });

                  parentLine.InvoiceProductBatches.push({
                    CartonOutstandingQty: 0,
                    CartonQty: 0,
                    CartonWeight: 0,
                    ChangedBy: undefined,
                    Code: undefined,
                    CreatedBy: undefined,
                    DateChanged: undefined,
                    DateCreated: undefined,
                    DespatchDate: new Date(),
                    InvoiceLineNo: parentLine.LineNo,
                    LotNo: undefined,
                    PackedOnDate: undefined,
                    PalletNo: undefined,
                    RecordID: undefined,
                    TotalWeight: 0,
                    WorkOrder: undefined,
                    PackSize: parentLine.PackSize,
                    SalesUMConversion: parentLine.SalesUMConversion,
                  });

                  this.setFooterItems(this.localDataSource._array);
                  setTimeout(() => {
                    dataGrid.refresh(true);
                  }, 10);
                }
              },
            },
          ],
          visible: this.baseView.mode === "create",
        },
      ];
    },

    // Footer
    getFooterItems() {
      return [
        {
          screenField: "SalesUMItemCount",
          dataField: "SalesUMQty",
          location: "left",
          value: 0,
        },
        {
          screenField: "TaxableFXTotal",
          dataField: "TaxableFXLineTotal",
          location: "right",
          label: "Sub Total (Customer Currency)",
          value: 0,
        },
        {
          screenField: "TaxFXTotal",
          dataField: "TaxFXLineTotal",
          location: "right",
          value: 0,
        },
        {
          screenField: "FXTotal",
          dataField: "FXLineTotal",
          location: "right",
          label: "Total (Customer Currency)",
          value: 0,
        },
      ];
    },

    footerLeftItems() {
      var leftItems = this.footerItems.filter((x) => x.location == "left");

      return leftItems
        .map((c) => {
          let globalField = { ...getGlobalField(c.screenField) };
          if (Object.keys(globalField).length == 0) return c;

          if (c.value != undefined) globalField.value = c.value;
          if (c.dataField != undefined) globalField.dataField = c.dataField;
          if (c.label != undefined) globalField.label = c.label;
          if (c.format != undefined) globalField.format = c.format;
          if (c.fieldType != undefined) globalField.fieldType = c.fieldType;
          if (c.length != undefined) globalField.length = c.length;
          if (globalField.type === "Decimal") {
            globalField.label = globalField.label.replace(
              "(Customer Currency)",
              `(${this.model.CurrencyCode})`
            );
          }
          if (globalField.type === "Decimal") {
            globalField.label = globalField.label.replace(
              "(Customer Currency)",
              `(${this.model.CurrencyCode})`
            );
          }
          if (globalField) return globalField;
          else return c;
        })
        .filter((x) => x != null);
    },
    footerRightItems() {
      var rightItems = this.footerItems.filter(
        (x) =>
          x.location == "right" &&
          (x.hasOwnProperty("visible") ? x.visible : true)
      );

      return rightItems
        .map((c) => {
          let globalField = { ...getGlobalField(c.screenField) };

          if (Object.keys(globalField).length == 0) return c;

          if (c.value != undefined) globalField.value = c.value;
          if (c.dataField != undefined) globalField.dataField = c.dataField;
          if (c.label != undefined) globalField.label = c.label;
          if (c.format != undefined) globalField.format = c.format;

          if (globalField.type === "Decimal") {
            globalField.label = globalField.label.replace(
              "(Customer Currency)",
              `(${this.model.CurrencyCode})`
            );
          }

          if (globalField) return globalField;
          else return c;
        })
        .filter((x) => x != null);
    },

    // Create Invoice
    IsCustomerEditable() {
      if (this.baseView.mode == "create") return true;
      else return false;
    },
    getCreateColumns() {
      return this.parseGridColumns([
        {
          caption: "Product / Account",
          dataField: "ItemCode",
          sortOrder:
            this.soDetailsCurrentView != "ProductList" &&
            this.soDetailsCurrentView != "GLList"
              ? "asc"
              : undefined,
          // sortIndex: 0,
          cssClass:
            this.baseView.mode == "create" &&
            (this.soDetailsCurrentView == "ProductList" ||
              this.soDetailsCurrentView == "GLList")
              ? "gc-editable-cell-color"
              : undefined,
          allowEditing:
            this.soDetailsCurrentView == "ProductList" ||
            this.soDetailsCurrentView == "GLList",
          allowSearch: true,
          allowSorting: true,
          minWidth: 420,
          editorOptions:
            this.soDetailsCurrentView == "ProductList"
              ? {
                  dataSource: ProductService.dataSourceConfig(
                    "api/ProductFilterGroupSaleItemCompanyBranch",
                    {
                      filter: [
                        ["ItemType", "=", `S`],
                        "and",
                        ["IsDefault", "=", "Y"],
                        "and",
                        ["AvailableForSale", "=", "Y"],
                        "and",
                        ["Inactive", "=", "N"],
                        "and",
                        ["CustomerCode", "=", `${this.model.CustomerCode}`],
                        "and",
                        ["BranchCode", "=", `${this.model.BranchCode}`],
                      ],
                      postProcess: (e) => {
                        e = this.ProductItemListPostProcess(e);
                        return e;
                      },
                    }
                  ),
                  displayExpr: "ItemCode",
                  valueExpr: "ItemCode",
                  searchTimeout: 10,
                  searchEnabled: true,
                  searchExpr: ["ItemCode", "Description"],
                  itemTemplate: (data) => {
                    return ` <div class="custom-auto-complete" id="${data.ItemCode}" >
                            <span class="auto-complete-primary">
                              ${data.ItemCode}
                            </span>
                            <span class="record-secondary gc-grid-code">
                              ${data.Description}
                            </span> 
                          </div>`;
                  },
                  dropDownOptions: {
                    minWidth: 400,
                  },
                  onSelectionChanged: (e) => {
                    this.onProductSelected(e);
                  },
                  onItemClick: (e, lineData) => {
                    this.onEnterItemCode({ event: { key: "Enter" } }, lineData);
                  },
                  onFocusIn: (e, cellInfo) => {
                    if (this.focusedCellData) {
                      this.removeCellFocus();
                    }
                    e.event.target.select();
                    this.onSalesUMQtyFocusIn(e, cellInfo);
                    e.element.parentElement.classList.add(
                      "key-pad-focused-cell"
                    );
                  },
                  onFocusOut: () => {
                    setTimeout(() => {
                      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
                      dataGrid.saveEditData();
                      dataGrid.refresh(true);

                      this.refreshDataSource();
                    }, 100);
                  },
                  onKeyDown: (e, cellInfo) => {
                    if (
                      e.event.key == "Enter" ||
                      (e.event.key == "Tab" && e.event.shiftKey == false)
                    ) {
                      if (cellInfo.data.SalesUMs.length == 1) {
                        setTimeout(() => {
                          this.onEnterItemCode(
                            { event: { key: "Enter" } },
                            cellInfo
                          );
                        }, 10);
                      }
                    }
                  },
                }
              : {
                  dataSource: ProductService.dataSourceConfig("api/GLChart", {
                    filter: [["Inactive", "=", `N`]],
                  }),
                  displayExpr: "ComputedStringCode",
                  valueExpr: "ComputedStringCode",
                  searchTimeout: 10,
                  searchEnabled: true,
                  searchExpr: ["ComputedStringCode", "Description"],
                  itemTemplate: (data) => {
                    return ` <div class="custom-auto-complete" id="${data.ComputedStringCode}" >
                            <span class="auto-complete-primary">
                              ${data.ComputedStringCode}
                            </span>
                            <span class="record-secondary gc-grid-code">
                              ${data.Description}
                            </span> 
                          </div>`;
                  },
                  dropDownOptions: {
                    minWidth: 400,
                  },
                  onSelectionChanged: (e) => {
                    this.onProductSelected(e);
                  },
                  onItemClick: (e, lineData) => {
                    this.onEnterItemCode({ event: { key: "Enter" } }, lineData);
                  },

                  onFocusIn: (e, cellInfo) => {
                    if (this.focusedCellData) {
                      this.removeCellFocus();
                    }
                    e.event.target.select();
                    this.onSalesUMQtyFocusIn(e, cellInfo);
                    e.element.parentElement.classList.add(
                      "key-pad-focused-cell"
                    );
                  },
                },
          setCellValue: async (newData, value, currentRowData) => {
            newData.ItemCode = value;
            newData.OriginItemCode = value;
            newData.ID = currentRowData.ID;

            if (this.soDetailsCurrentView == "ProductList") {
              let index = this.selectedProducts.findIndex(
                (x) => x.ItemCode == value
              );
              if (index != -1) {
                newData.Description = this.selectedProducts[index].Description;
                newData.SalesUM = this.selectedProducts[index].SalesUM;
                newData.IsBatchProduct =
                  this.selectedProducts[index].IsBatchProduct;
                newData.ItemType = this.selectedProducts[index].ItemType;

                if (this.selectedProducts[index].IsBatchProduct != "No") {
                  newData.InvoiceProductBatches = [];
                  newData.InvoiceProductBatches.push({
                    BatchNo: undefined,
                    ItemCode: newData.ItemCode,
                    StockUMBatchQty: 0,
                    SalesUMBatchQty: 0,
                    SalesUMInvoiceConversion: 0,
                    SupplierCode: undefined,
                    SupplierName: undefined,
                    // InvoiceLineNo: parentLine.LineNo,
                    SalesUMCreditedQty: 0,
                    UseByDate: undefined,
                    BatchLineNo: 1,
                    AvailableQty: 0,
                    AllocatedQty: 0,
                    ParentID: currentRowData.ID,
                  });

                  ProductBatchService.getBatchDataItems(
                    `ItemCode eq '${newData.ItemCode}'  and AvailableQty gt 0`
                  ).then((res) => {
                    res.data.value.forEach((item) => {
                      item.ItemCode = newData.ItemCode;
                      item.ParentID = currentRowData.ID;
                      this.addBatchItemsToCollections(item);
                    });
                  });
                }
              }
            } else {
              let index = this.selectedProducts.findIndex(
                (x) => x.Code == value
              );
              if (index != -1) {
                newData.Description = this.selectedProducts[index].Description;
                newData.SalesUM = "Ea";
                newData.IsBatchProduct = "No";
                newData.ItemType = "G";
              }
            }

            if (this.SelectedCustomerData) {
              if (this.SelectedCustomerData.Gst.toLowerCase() == "yes") {
                newData.TaxCode = "G";
              } else newData.TaxCode = "E";
            } else newData.TaxCode = "E";

            var tax = this.taxCodeDataSourceItems.filter(
              (t) => t.Code == newData.TaxCode
            );
            if (tax.length > 0) newData.SalesTaxPcnt = tax[0].Rate;

            if (newData.ItemCode && !currentRowData.ItemCode) this.addNewRow();

            newData = this.productListPostProcess([newData])[0];

            this.updateDataSource(newData);

            // return newData;
          },
          cellTemplate: "Record",
          cellTemplateRecord: {
            primary: [
              {
                dataField: "Description",
              },
            ],
            secondary: [
              {
                dataField: "ItemCode",
              },
            ],
          },
        },
        {
          caption: "Description",
          dataField: "Description",
          allowEditing: false,
          visible: false,
          showInColumnChooser: false,
        },
        {
          caption: "Tax",
          dataField: "TaxCode",
          allowEditing: this.soDetailsCurrentView == "GLList",
          cssClass:
            this.baseView.mode == "create"
              ? "edit-cell-textbox-field-1 gc-editable-cell-color"
              : undefined,
          setCellValue: (newData, value, currentRowData) => {
            newData.TaxCode = value;
            newData.ID = currentRowData.ID;

            var tax = this.taxCodeDataSourceItems.filter(
              (t) => t.Code == newData.TaxCode
            );
            if (tax.length > 0) newData.SalesTaxPcnt = tax[0].Rate;

            var obj = { ...currentRowData, ...newData };

            obj = SalesOrderState.setStockPrices(obj);
            obj = SalesOrderState.setLineTotals(obj, this.model);

            newData = obj;
            this.updateDataSource(newData, undefined, true);

            return newData;
          },
          visible: this.soDetailsCurrentView == "GLList",
          showInColumnChooser: this.soDetailsCurrentView == "GLList",
          editorType: "dxSelectBox",
          editorOptions: {
            items: this.taxCodeDataSourceItems,
            displayExpr: "Code",
            valueExpr: "Code",
            searchExpr: ["Code", "Description"],
            searchMode: "contains",
            searchEnabled: true,
            searchTimeout: 10,
            // showClearButton: true,
            itemTemplate: (data) => {
              return ` <div class="custom-auto-complete">
                        <span class="auto-complete-primary">
                          ${data.Description}
                        </span>
                        <span class="record-secondary gc-grid-code">
                          ${data.Code}
                        </span>
                      </div>`;
            },
            dropDownOptions: {
              width: 200,
            },
            onFocusIn: (e, cellInfo) => {
              if (this.focusedCellData) {
                this.removeCellFocus();
              }
              e.event.target.select();
              this.onSalesUMQtyFocusIn(e, cellInfo);
              e.element.parentElement.classList.add("key-pad-focused-cell");
            },

            onKeyDown: (e, cellInfo) => {
              if (
                e.event.key == "Enter" ||
                (e.event.key == "Tab" && e.event.shiftKey == false)
              ) {
                if (this.soDetailsCurrentView == "GLList") {
                  e.event.preventDefault();
                  this.focusOnEditableCell(cellInfo.rowIndex, "SalesUMQty");
                }
              }
            },
          },
        },
        {
          caption: "UM",
          dataField: "SalesUM",
          // showEditorAlways: this.dataViewType != "review",
          allowEditing: this.baseView.mode == "create",
          allowSorting: false,
          cssClass:
            this.baseView.mode == "create"
              ? "edit-cell-textbox-field-1 gc-editable-cell-color"
              : undefined,
          editorOptions: {
            showClearButton: false,
            searchEnabled: true,
            dropDownOptions: {
              minWidth: 150,
            },
            onFocusIn: (e, cellInfo) => {
              if (cellInfo.data.SalesUMs.length == 1) {
                setTimeout(() => {
                  this.onEnterItemCode({ event: { key: "Enter" } }, cellInfo);
                }, 10);
              } else {
                if (this.focusedCellData) {
                  this.removeCellFocus();
                }
                e.event.target.select();
                this.onSalesUMQtyFocusIn(e, cellInfo);
                e.element.parentElement.classList.add("key-pad-focused-cell");
              }
            },
          },
          calculateDisplayValue: (e) => {
            return e.SalesUM;
          },
          setCellValue: async (newData, value, currentRowData) => {
            newData.SalesUM = value;
            newData.SalesUMQty = currentRowData.SalesUMQty;
            var obj = { ...currentRowData, ...newData };

            if (newData.SalesUMQty > 0) {
              if (this.allowPriceChangeTriggerConfirmation) {
                let confirmPriceChange = await this.$confirm(
                  `Would you like to recalculate the sell price of ${currentRowData.Description} based upon this new UM ?`,
                  "Price Change"
                );

                if (confirmPriceChange) {
                  return this.updateThePriceDataOnUMChange(
                    newData,
                    currentRowData,
                    obj
                  );
                }
              } else {
                return this.updateThePriceDataOnUMChange(
                  newData,
                  currentRowData,
                  obj
                );
              }
            }
          },
          lookup: {
            dataSource: (data) => {
              if (Object.keys(data).length != 0 && data && data.data) {
                return data.data.SalesUMs;
              } else return [];
            },
            displayExpr: "SalesUM",
            valueExpr: "SalesUM",
          },
          width: ColumnWidth.UnitOfMeasure.width,
          maxWidth: ColumnWidth.UnitOfMeasure.minWidth,
        },
        // Quantity
        {
          caption: "Quantity",
          dataField: "SalesUMQty",
          allowEditing: true,
          allowSorting: false,
          allowSearch: false,
          // showEditorAlways: this.baseView.mode != "review",
          format: "##,###,###,###,##0.0000",
          dataType: "number",
          editorType: "dxNumberBox",
          cssClass:
            this.baseView.mode != "review"
              ? "edit-cell-numberbox-field gc-editable-cell-color qty-cell"
              : undefined,
          // editCellTemplate: "EditableDecimal",
          editorOptions: {
            // tabIndex: 1,
            format: "##,###,###,###,##0.0000",
            elementAttr: {
              class: "gc-grid-number-box-editor",
            },
            // eslint-disable-next-line no-unused-vars
            onFocusIn: (e, cellInfo) => {
              if (this.focusedCellData) {
                this.removeCellFocus();
              }
              e.event.target.select();
              // this.onSalesUMQtyFocusIn(e, cellInfo);
              e.element.parentElement.classList.add("key-pad-focused-cell");
            },
            onKeyDown: (e, cellInfo) => {
              if (e.event.key == "Tab" && e.event.shiftKey == true) {
                if (this.soDetailsCurrentView == "GLList") {
                  e.event.preventDefault();
                  this.focusOnEditableCell(cellInfo.rowIndex, "TaxCode");
                } else if (
                  this.soDetailsCurrentView == "ProductList" &&
                  cellInfo.data.SalesUMs.length == 1
                ) {
                  e.event.preventDefault();
                  this.focusOnEditableCell(cellInfo.rowIndex, "ItemCode");
                }
              }
            },
          },
          setCellValue: async (newData, value, currentRowData) => {
            newData.SalesUMQty = value;
            newData.ID = currentRowData.ID;

            if (
              this.soDetailsCurrentView != "GLList" &&
              currentRowData.ItemType == "S"
            )
              this.updatePriceData(
                { ...currentRowData, ...newData },
                currentRowData
              );
            else {
              if (currentRowData.SalesUMFXPrice != 0) {
                var obj = { ...currentRowData, ...newData };

                obj = SalesOrderState.setStockPrices(obj);
                obj = SalesOrderState.setLineTotals(obj, this.model);

                newData = obj;
                this.updateDataSource(newData, undefined, true);
              }
            }

            return newData;
          },
          width: ColumnWidth.Quantity4DecimalPoints.width,
          maxWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
        },
        {
          caption: `Price (${this.model.CurrencyCode})`,
          dataField: "SalesUMFXPrice",
          allowEditing: FeatureStorage.checkPermission(
            "SalesOrderDetailEditPrice",
            "edit"
          ),
          // showEditorAlways: this.baseView.mode != "review",
          allowSorting: false,
          allowSearch: false,
          format: "##,###,###,###,##0.0000",
          dataType: "number",
          cssClass: FeatureStorage.checkPermission(
            "SalesOrderDetailEditPrice",
            "edit"
          )
            ? "edit-cell-numberbox-field gc-editable-cell-color"
            : undefined,
          editorOptions: {
            format: "##,###,###,###,##0.0000",
            elementAttr: {
              class: "gc-grid-number-box-editor",
            },
            mode: "number",
            onFocusIn: (e, cellInfo) => {
              if (this.focusedCellData) {
                this.removeCellFocus();
              }
              e.element.parentElement.classList.add("key-pad-focused-cell");
              e.event.target.select();
              this.onSalesUMQtyFocusIn(e, cellInfo);
            },
          },
          setCellValue: async (newData, value, currentRowData) => {
            newData.SalesUMFXPrice = value;
            newData.ID = currentRowData.ID;

            newData = await this.onPriceUpdateValidateGPToolPrcnt(
              newData,
              value,
              currentRowData
            );

            this.addToReview(newData);
            this.updateDataSource(newData);

            return newData;
          },
          width: ColumnWidth.Price4DecimalPoints.width,
          maxWidth: ColumnWidth.Price4DecimalPoints.minWidth,
        },
        {
          caption: "Discount",
          dataField: "DiscPcnt",
          allowEditing: FeatureStorage.checkPermission(
            "SalesOrderDetailEditDiscount",
            "edit"
          ),
          visible: FeatureStorage.checkPermission(
            "SalesOrderDetailEditDiscount",
            "edit"
          ),
          showInColumnChooser: FeatureStorage.checkPermission(
            "SalesOrderDetailEditDiscount",
            "edit"
          ),
          allowSorting: false,
          allowSearch: false,
          format: "##0.00 '%'",
          cssClass: FeatureStorage.checkPermission(
            "SalesOrderDetailEditDiscount",
            "edit"
          )
            ? "edit-cell-numberbox-field gc-editable-cell-color"
            : undefined,
          editorOptions: {
            format: "##0.00 '%'",
            elementAttr: {
              class: "gc-grid-number-box-editor",
            },
            onFocusIn: (e, cellInfo) => {
              if (this.focusedCellData) {
                this.removeCellFocus();
              }
              e.element.parentElement.classList.add("key-pad-focused-cell");
              e.event.target.select();
              this.onSalesUMQtyFocusIn(e, cellInfo);
            },
          },
          setCellValue: async (newData, value, currentRowData) => {
            newData.DiscPcnt = value;

            newData = this.onDiscountPrctUpdate(newData, value, currentRowData);
            return newData;
          },
          width: ColumnWidth.Discount.width,
          maxWidth: ColumnWidth.Discount.minWidth,
        },
        {
          caption: `Ex Total (${this.model.CurrencyCode})`,
          dataField: "TaxableFXLineTotal",
          allowEditing: false,
          format: "##,###,###,###,##0.00",
          allowSorting: false,
          allowSearch: false,
          width: ColumnWidth.Price2DecimalPoints.width,
          maxWidth: ColumnWidth.Price2DecimalPoints.minWidth,
        },
        {
          caption: `Tax (${this.model.CurrencyCode})`,
          dataField: "TaxFXLineTotal",
          allowEditing: false,
          format: "##,###,###,###,##0.00",
          allowSorting: false,
          allowSearch: false,
          width: ColumnWidth.Price2DecimalPoints.width,
          maxWidth: ColumnWidth.Price2DecimalPoints.minWidth,
        },
        {
          caption: `Inc Total (${this.model.CurrencyCode})`,
          dataField: "FXLineTotal",
          allowEditing: false,
          format: "##,###,###,###,##0.00",
          allowSorting: false,
          allowSearch: false,
          width: ColumnWidth.Price2DecimalPoints.width,
          maxWidth: ColumnWidth.Price2DecimalPoints.minWidth,
        },
        {
          screenField: "Comments",
          dataField: "Comment",
          allowEditing: true,
          minWidth: ColumnWidth.Comment.minWidth,
          width: ColumnWidth.Comment.width,
          cssClass:
            this.baseView.mode != "review"
              ? "gc-editable-cell-color"
              : undefined,
          editorType: "dxTextArea",
          setCellValue: async (newData, value, currentRowData) => {
            newData.Comment = value;

            this.addToReview({ ...currentRowData, ...newData });
            this.updateDataSource({ ...currentRowData, ...newData });
            return newData;
          },
          editorOptions: {
            onFocusIn: (e, cellInfo) => {
              if (this.focusedCellData) {
                this.removeCellFocus();
              }
              e.element.parentElement.classList.add("key-pad-focused-cell");
              e.event.target.select();
              this.onSalesUMQtyFocusIn(e, cellInfo);
            },
          },
        },
      ]);
    },
  },
  methods: {
    onQuantityChanged(e, data) {
      data.setValue(e.value);
    },
    // Create Invoice
    // This function is triggered when a customer is selected. It updates the model with the selected customer's data and loads the customer's items.
    async onCustomerValueSelected(e) {
      // Store the selected customer's data in SelectedCustomerData
      this.SelectedCustomerData = e.selectedItem;

      // Update the model's CurrencyCode with the selected customer's CurrencyCode
      this.model.CurrencyCode = this.SelectedCustomerData.CurrencyCode;

      // Update the model's CustomerCode with the selected customer's Code
      this.model.CustomerCode = this.SelectedCustomerData.Code;
      this.model.CustomerName = this.SelectedCustomerData.Name;

      // Call the updateDates function to update the dates based on the selected customer's data
      this.updateDates();

      // Call the getCustomerAgent function to get the agent of the selected customer
      this.getCustomerAgent();

      // Call the loadCustomerItems function to load the items of the selected customer
      this.customerDataSourceItems.clear();
      this.loadCustomerItems(undefined, 0);

      // Call the triggerPriceChange function to trigger a price change for the customer
      this.triggerPriceChange("Customer");

      // Set the soDetailsCurrentView to "CustomerList" to change the current view to the customer list
      this.soDetailsCurrentView = "CustomerList";

      // Call the setDeliveryAddressDataSourceItems function to set the delivery address data source items
      // Await is used to ensure that the function completes before the next line of code is executed
      await this.setDeliveryAddressDataSourceItems(this.model.CustomerCode);

      this.setExchangeRateProperties();
    },
    // This asynchronous function sets the delivery address data source items based on the provided customer code and line number
    async setDeliveryAddressDataSourceItems(customerCode, lineNo) {
      // Set default line number to 1
      let defaultLineNo = 1;
      // If line number is provided, set default line number to the provided line number
      if (lineNo) defaultLineNo = +lineNo;

      // Configure the data source for the customer delivery address
      let deliveryAddresDs = CustomerDeliveryAddressService.dataSourceConfig(
        "api/CustomerDeliveryAddress",
        {
          // Select specific fields from the data source
          select: [
            "CompanyName",
            "PhysicalAddressStreetLine1",
            "PhysicalAddressStreetLine2",
            "PhysicalAddressSuburb",
            "PhysicalAddressState",
            "PhysicalAddressPostcode",
            "PhysicalAddressCountry",
            "RecordId",
            "LineNo",
            "TransitDays",
          ],
          // Set the key field to "RecordId"
          key: "RecordId",
          // Set the filter conditions to only include active records for the provided customer code and line number
          filter: [
            ["Inactive", "=", "N"],
            "and",
            ["CustomerCode", "=", customerCode],
            "and",
            ["LineNo", "=", defaultLineNo],
          ],
        }
      );

      // Load the data from the data source
      let results = await deliveryAddresDs.load();

      // If there are results
      if (results.length > 0) {
        // Get the first record
        let deliveryAddressLine1Record = results[0];

        // If the record exists
        if (deliveryAddressLine1Record) {
          // Set the model's delivery address fields to the values from the record
          this.model.DeliveryAddressCompanyName =
            deliveryAddressLine1Record.CompanyName;
          this.model.DeliveryAddressCountry =
            deliveryAddressLine1Record.PhysicalAddressCountry;
          this.model.DeliveryAddressPostcode =
            deliveryAddressLine1Record.PhysicalAddressPostcode;
          this.model.DeliveryAddressState =
            deliveryAddressLine1Record.PhysicalAddressState;
          this.model.DeliveryAddressStreetLine1 =
            deliveryAddressLine1Record.PhysicalAddressStreetLine1;
          this.model.DeliveryAddressStreetLine2 =
            deliveryAddressLine1Record.PhysicalAddressStreetLine2;
          this.model.DeliveryAddressSuburb =
            deliveryAddressLine1Record.PhysicalAddressSuburb;
          this.model.CustomerDeliveryAddressLineNo =
            deliveryAddressLine1Record.LineNo;
          this.TransitDays = deliveryAddressLine1Record.TransitDays;
        }
      } else {
        // If there are no results, set the model's delivery address fields to undefined and TransitDays to 0
        this.model.DeliveryAddressCompanyName = undefined;
        this.model.DeliveryAddressCountry = undefined;
        this.model.DeliveryAddressPostcode = undefined;
        this.model.DeliveryAddressState = undefined;
        this.model.DeliveryAddressStreetLine1 = undefined;
        this.model.DeliveryAddressStreetLine2 = undefined;
        this.model.DeliveryAddressSuburb = undefined;
        this.model.CustomerDeliveryAddressLineNo = undefined;
        this.TransitDays = 0;
      }
    },

    async setExchangeRateProperties() {
      if (this.model.CompanyCurrency != this.model.CurrencyCode) {
        let result = await CompanyCurrencyService.getByCurrencyCode(
          this.model.CurrencyCode
        );
        this.model.ExchangeRate = result.data.value[0].BuyRate;
      } else {
        this.model.ExchangeRate = 1;
      }
    },
    // This function updates the dates in the model based on the selected customer's data
    updateDates() {
      // Store the selected customer's data
      var data = this.SelectedCustomerData;
      var newDate = undefined;
      // Get the year and month of the date in the model
      var year = new Date(this.model.DateIn).getFullYear();
      var month = new Date(this.model.DateIn).getMonth();
      // Calculate the new date based on the selected customer's term calculation
      if (data.TermCalculation == "Actual") {
        newDate = new Date(this.model.DateIn).addDays(data.Terms);
      } else if (data.TermCalculation == "EOM") {
        newDate = new Date(year, month + data.Terms / 30 + 1, 0);
      } else if (data.TermCalculation == "EOW") {
        var endDate = new Date(this.model.DateIn).getEndOfWeekDate();
        newDate = new Date(endDate).addDays(data.Terms);
      } else {
        newDate = this.model.DateIn;
      }
      // Update the dates in the model with the new date
      this.model.DateDue =
        this.model.RequiredByDate =
        this.model.ShipDate =
          newDate;
    },
    // This function gets the agent of the selected customer and updates the model with the agent's data
    getCustomerAgent() {
      if (this.model.CustomerCode)
        CustomerAgentService.getByCustomerCode(this.model.CustomerCode, [
          "AgentCode",
          "CustomerCode",
          "CommissionPcnt",
        ]).then((res) => {
          if (res.data.value.length > 0) {
            this.model.AgentName = res.data.value[0].Supplier.Name;
            this.model.AgentCommissionPcnt = res.data.value[0].CommissionPcnt;
          }
        });
    },
    // This function sets the default sales representative in the model
    async setDefaultSalesRep() {
      // Get the feature items
      var featureItems = FeatureStorage.getItems();
      // Check if the first feature item's ApplicationUserTypeCode is "Sales"
      if (featureItems[0].ApplicationUserTypeCode == "Sales") {
        // Get the user's default sales representative
        await SalesRepresentativeService.getUserDefaultSalesRepresentative({
          select: ["SalesRepresentativeCode"],
        }).then((res) => {
          // If there is a sales representative, set it in the model
          if (res.data.value.length > 0)
            this.model.SalesRepresentativeCode =
              res.data.value[0].SalesRepresentativeCode;
        });
      } else {
        // If the ApplicationUserTypeCode is not "Sales", get the factor by code 95
        if (getFactorByCode(95).Factorc) {
          // If there is a factor, set it in the model
          this.model.SalesRepresentativeCode = getFactorByCode(95).Factorc;
        } else {
          // If there is no factor, get the customer by code
          await CustomerService.getByCode(this.baseView.id, [
            "Code",
            "SalesRepresentativeCode",
          ])
            .then((res) => {
              // If there is a customer, set the sales representative in the model
              if (res.data.value.length > 0)
                this.model.SalesRepresentativeCode =
                  res.data.value[0].SalesRepresentativeCode;
            })
            .catch((err) => {
              // If there is an error, set the sales representative in the model to undefined
              if (err) {
                this.model.SalesRepresentativeCode = undefined;
              }
            });
        }
      }
    },
    // This function is triggered when a row in the delivery to table is clicked
    async onDeliverToRowClick(e) {
      // Get the clicked row's data
      var deliveryAddressLine1Record = e.itemData;

      // Set the delivery address in the model based on the clicked row's data
      this.model.DeliveryAddressCompanyName =
        deliveryAddressLine1Record.CompanyName;
      this.model.DeliveryAddressCountry =
        deliveryAddressLine1Record.PhysicalAddressCountry;
      this.model.DeliveryAddressPostcode =
        deliveryAddressLine1Record.PhysicalAddressPostcode;
      this.model.DeliveryAddressState =
        deliveryAddressLine1Record.PhysicalAddressState;
      this.model.DeliveryAddressStreetLine1 =
        deliveryAddressLine1Record.PhysicalAddressStreetLine1;
      this.model.DeliveryAddressStreetLine2 =
        deliveryAddressLine1Record.PhysicalAddressStreetLine2;
      this.model.DeliveryAddressSuburb =
        deliveryAddressLine1Record.PhysicalAddressSuburb;
      this.model.CustomerDeliveryAddressLineNo =
        deliveryAddressLine1Record.LineNo;
    },
    // This function loads the customer's items
    loadCustomerItems(totalItemsCount, skip) {
      // Define the filter for the items
      var filter = `ItemType eq 'S' and IsDefault eq 'Y' and CustomerCode eq '${this.model.CustomerCode}' and Inactive eq 'N'`;

      // If there is a branch code, add it to the filter
      if (this.model.BranchCode) {
        filter = filter + ` and BranchCode eq '${this.model.BranchCode}'`;
      }

      // If there is no total items count or the skip is less than the total items count, get the custom data
      if (!totalItemsCount || skip < totalItemsCount)
        ProductService.getCustomData({
          url: "api/CustomerSaleItemCompanyBranch",
          count: true,
          skip: skip,
          top: 1000,
          filter: filter,
          orderby: "Description asc",
          select: [
            "ItemType",
            "IsDefault",
            "AvailableForSale",
            "CustomerCode",
            "Inactive",
            "Available",
            "BranchCode",
            "Description",
            "HasPrimaryImage",
            "IsBatchProduct",
            "ItemCode",
            "ItemType",
            "ProductCategory1Code",
            "ProductCategory2Code",
            "ProductCategory3Code",
            "ProductCategory4Code",
            "ProductTypeCode",
            "SalesUM",
            "StandardSell",
            "Status",
            "StockUM",
          ],
        }).then((res) => {
          if (!totalItemsCount) totalItemsCount = res.data["@odata.count"];
          var batchProdCodes = [];
          res.data.value.forEach((element, index) => {
            // Set properties of the element
            element.OriginItemCode = element.ItemCode;
            element.ProductCode = element.ItemCode;

            element.HasPromotion = false;
            element.Origin = "Customer";
            element.SalesUMQty = 0;
            element.SalesUMFXPrice = 0;
            element.TaxableFXLineTotal = 0;
            element.TaxFXLineTotal = 0;
            element.FXLineTotal = 0;
            element = { ...soDetailsBaseRecord, ...element };
            element.LineNo = index + 1;
            element.ID = element.ItemCode + "-" + element.LineNo;

            // If the element is a batch product
            if (element.IsBatchProduct != "No") {
              element.InvoiceProductBatches = [];
              batchProdCodes.push(element.ItemCode);
              element.InvoiceProductBatches.push({
                BatchNo: undefined,
                ItemCode: element.ItemCode,
                StockUMBatchQty: 0,
                SalesUMBatchQty: 0,
                SalesUMInvoiceConversion: 0,
                SupplierCode: undefined,
                SupplierName: undefined,
                // InvoiceLineNo: parentLine.LineNo,
                SalesUMCreditedQty: 0,
                UseByDate: undefined,
                BatchLineNo: 1,
                AvailableQty: 0,
                AllocatedQty: 0,
                ParentID: element.ID,
              });
              // Get batch items for the product code
              ProductBatchService.getBatchDataItems(
                `ItemCode eq '${element.ItemCode}' and AvailableQty gt 0`
              ).then((res) => {
                res.data.value.forEach((item) => {
                  item.ItemCode = element.ProductCode;
                  item.ParentID = element.ID;
                  this.addBatchItemsToCollections(item);
                });
              });
            }

            // Insert the element into the customer data source items
            this.customerDataSourceItems.insert(element);
          });

          // If there are more items to load, call the function recursively
          if (totalItemsCount > 0)
            this.loadCustomerItems(totalItemsCount, skip + 1000);
        });
      else return;
    },
    productListPostProcess(data) {
      var items = [];

      if (data) {
        data.forEach((element) => {
          // Find the index of the element in reviewDataList._array
          var i = this.reviewDataList._array.findIndex((x) => {
            if (x.LineNo > 0) {
              return x.ID == element.ID + "-" + x.LineNo;
            } else return x.ID == element.ID;
          });

          var item = undefined;
          var ums = [];
          // If the element is not found, use the input element
          if (i == -1) item = element;
          else {
            // Otherwise, use the existing item
            item = this.reviewDataList._array[i];
          }

          // If the element is not a batch product, set IsBatchProduct to "No"
          if (!element.IsBatchProduct) element.IsBatchProduct = "No";

          // Set properties of the item based on soDetailsCurrentView
          if (this.soDetailsCurrentView == "GLList") {
            item.SLUMLength = 0;
            item.SalesUMs = [];
          } else {
            ums = this.salesUMsLocalDictionary[item.OriginItemCode]
              ? this.salesUMsLocalDictionary[item.OriginItemCode]
              : [];
            item.SLUMLength = ums.length;
            item.SalesUMs = ums;
          }
          // Add the item to the items array
          items.push(item);

          // Check localCopyItems for any items that match the current item
          this.localCopyItems.forEach((copy) => {
            if (
              copy.OriginItemCode == item.ItemCode ||
              copy.OriginItemCode == item.OriginItemCode
            ) {
              // Find the index of the copy in reviewDataList._array
              var c_i = this.reviewDataList._array.findIndex(
                (x) => x.ID == copy.ID
              );

              // If the copy is found, add it to the items array
              if (c_i != -1) {
                items.push(this.reviewDataList._array[c_i]);
              } else {
                // Otherwise, add the copy to the items array
                items.push(copy);
              }
            }
          });
        });

        // Return the items array
        return items;
      } else {
        // If data is not defined, return an empty array
        return [];
      }
    },
    ProductItemListPostProcess(data) {
      data.forEach((element) => {
        var ums = this.salesUMsLocalDictionary[element.ItemCode]
          ? this.salesUMsLocalDictionary[element.ItemCode]
          : [];
        element.SLUMLength = ums.length;
        element.SalesUMs = ums;
      });

      return data;
    },
    // This is an asynchronous function that is triggered when the SalesUMQty field gains focus.
    async onSalesUMQtyFocusIn(e, cellInfo) {
      // It first finds the input element within the event's target element.
      let inputEl = e.element.querySelector(".dx-texteditor-input");

      // It then stores the event, cell information, a flag indicating whether a request has been sent, and the input element in the component's data.
      this.focusedCellData = {
        e: e,
        cellInfo: cellInfo,
        requestSent: false,
        inputEl: inputEl,
      };

      // It then calls the applyFocusClass method, passing the input element as an argument.
      this.applyFocusClass(inputEl);
    },
    // This method applies a focus class to the cell that is currently focused.
    applyFocusClass(inputEl) {
      // If there is no focused cell data, it returns immediately.
      if (!this.focusedCellData) return;

      // If no input element is provided, it uses the input element from the focused cell data.
      if (!inputEl) inputEl = this.focusedCellData.inputEl;

      // It gets the instance of the data grid component.
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;

      // It gets the cell element using the row index and column data field from the focused cell data.
      var cell = dataGrid.getCellElement(
        this.focusedCellData.cellInfo.rowIndex,
        this.focusedCellData.cellInfo.column.dataField
      );

      // It puts the cell into edit mode.
      dataGrid.editCell(
        this.focusedCellData.cellInfo.rowIndex,
        this.focusedCellData.cellInfo.column.dataField
      );

      // If the cell exists, it adds the "key-pad-focused-cell" class to it.
      if (cell) cell.classList.add("key-pad-focused-cell");
    },
    // This asynchronous function fetches sales units of measure (UMs) for a specific branch.
    async getSalesUMs(branchCode) {
      // It calls a service method to get the sales items for the given branch code.
      await SalesOrderV3Service.getSaleItemCompanyBranchProduct(
        branchCode
      ).then((res) => {
        // It then iterates over the returned items.
        for (const item of res.data.value) {
          // If the item's code is not already in the local dictionary, it adds it.
          if (this.salesUMsLocalDictionary[item.ItemCode] === undefined) {
            this.salesUMsLocalDictionary[item.ItemCode] = [
              {
                BranchCode: item.BranchCode,
                ItemCode: item.ItemCode,
                ItemType: item.ItemType,
                SalesUM: item.SalesUM,
              },
            ];
          } else {
            // If the item's code is already in the local dictionary, it adds the new item to the existing array.
            this.salesUMsLocalDictionary[item.ItemCode].push({
              BranchCode: item.BranchCode,
              ItemCode: item.ItemCode,
              ItemType: item.ItemType,
              SalesUM: item.SalesUM,
            });
          }
        }
      });
    },
    // This asynchronous function updates the price data for a sales order item.
    async updatePriceData(newData, rowData) {
      // If there is a timeout ID, it clears the timeout.
      if (this.timeOutID) clearTimeout(this.timeOutID);
      // If no row data is provided, it uses the new data as the row data.
      if (!rowData) rowData = newData;

      // It sets a timeout to delay the execution of the following code.
      this.timeOutID = setTimeout(() => {
        // It calls a service method to set the sale item price, passing the necessary parameters.
        SalesOrderV3Service.setSaleItemPrice(
          newData,
          rowData,
          this.model.SalesOrderTypeCode,
          this.model.BranchCode,
          this.model.CustomerCode,
          this.model.ExchangeRate,
          this.model.DateIn,
          this.model.RequiredByDate,
          false
        ).then(async (res) => {
          // It gets the instance of the data grid component and the row index of the updated item.
          var dataGrid = this.$refs["dx-data-grid-comp"].instance;
          var rowIndex = dataGrid.getRowIndexByKey(rowData.ID);

          // If the focused cell is the SalesUMFXPrice cell, it updates the cell value and puts the cell into edit mode.
          if (
            this.focusedCellData &&
            this.focusedCellData.cellInfo &&
            this.focusedCellData.cellInfo.column.dataField == "SalesUMFXPrice"
          ) {
            setTimeout(() => {
              dataGrid.cellValue(
                rowIndex,
                "SalesUMFXPrice",
                res.SalesUMFXPrice
              );

              var cell = dataGrid.getCellElement(rowIndex, "SalesUMFXPrice");
              dataGrid.editCell(rowIndex, "SalesUMFXPrice");

              // It then focuses the cell on the next Vue.js tick.
              this.$nextTick(() => {
                dataGrid.focus(cell);
              });
            }, 100);
          }

          // It merges the row data and the response data.
          res = { ...rowData, ...res };

          // Depending on the current view and the origin of the row data, it updates the appropriate data source and repaints the row.
          if (
            this.soDetailsCurrentView == "CustomerList" ||
            rowData.Origin == "Customer"
          ) {
            this.customerDataSourceItems.update(res.ID, res).then((data) => {
              dataGrid.repaintRows([rowIndex]);
              this.addToReview(data);
            });
          } else if (
            this.soDetailsCurrentView == "ProductList" ||
            rowData.Origin == "Product"
          ) {
            this.productDataSourceItems.update(res.ID, res).then((data) => {
              dataGrid.repaintRows([rowIndex]);
              this.addToReview(data);
            });
          } else {
            this.reviewDataList.update(res.ID, res).then((data) => {
              dataGrid.repaintRows([rowIndex]);
              this.addToReview(data);
            });
          }
        });
      }, 250);
    },
    // This function updates the data source
    updateDataSource(newData, currentRowData, refresh) {
      // Define a variable to hold the result
      var res = undefined;
      // If there is current row data, merge it with the new data
      if (currentRowData) res = { ...currentRowData, ...newData };
      // If there is no current row data, use the new data
      else res = newData;

      // If the current view is "CustomerList" or the origin of the result is "Customer"
      if (
        this.soDetailsCurrentView == "CustomerList" ||
        res.Origin == "Customer"
      ) {
        // Update the customer data source items with the result
        this.customerDataSourceItems.update(res.ID, res).then((data) => {
          this.dataSourceSaveAddToReview(res, data, refresh);
        });
      } else if (
        this.soDetailsCurrentView == "ProductList" ||
        res.Origin == "Product"
      ) {
        // Update the product data source items with the result
        this.productDataSourceItems.update(res.ID, res).then((data) => {
          this.dataSourceSaveAddToReview(res, data, refresh);
        });
      } else if (
        this.soDetailsCurrentView == "GLList" ||
        res.Origin == "GLItem"
      ) {
        // Update the GL list data source items with the result
        this.glListDataSourceItems.update(res.ID, res).then((data) => {
          this.dataSourceSaveAddToReview(res, data, refresh);
        });
      } else {
        // Update the review data list with the result and refresh the data source
        this.reviewDataList.update(res.ID, res);
        this.refreshDataSource();
      }
    },
    dataSourceSaveAddToReview(res, data, refresh) {
      // Get the instance of the data grid component
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;

      dataGrid.saveEditData().then(() => {
        setTimeout(() => {
          // Get the row index by the result ID and repaint the row
          var rowIndex = dataGrid.getRowIndexByKey(res.ID);
          dataGrid.repaintRows([rowIndex]);

          // Add the data to the review
          this.addToReview(data);
          // If refresh is true, refresh the data grid and the data source
          if (refresh) {
            dataGrid.refresh(true);
            this.refreshDataSource();
          }
        }, 10);
      });
    },
    async onDiscountPrctUpdate(newData, value, currentRowData) {
      // If there is a discount timeout, clear it
      if (this.discountTimeOut) clearTimeout(this.discountTimeOut);

      // Calculate the sales UM net FX price
      var SalesUMNetFXPrice = calculateSalesUMNetFXPrice(
        currentRowData.SalesUMFXPrice,
        value
      );

      // Calculate the new sales UM net price from the sales UM net FX price
      let newsalesUMNetPrice = calculateSalesUMNetPriceFromSalesUMNetFXPrice(
        SalesUMNetFXPrice,
        this.model.ExchangeRate
      );

      // Calculate the temporary gross profit percentage
      let tempGp = calculateGrossProfitPcnt(
        currentRowData.SalesUMStandardCost,
        newsalesUMNetPrice
      );

      // Get the global GP value
      let globalGPVal = parseFloat(getFactorByCode(571).Factorc);

      // If the global GP value is not 0 and the temporary GP is less than the global GP value or greater than 100
      if (globalGPVal != 0 && (tempGp < globalGPVal || tempGp > 100)) {
        // If the sales UM quantity is not 0
        if (currentRowData.SalesUMQty != 0) {
          // Show an alert
          let res = await alert(
            `Net Price is below the minimum GP of ${
              globalGPVal.roundNumber(2) + " %"
            }. Please enter a discount that meets the minimum GP % requirement.`,
            "Validation Error"
          );

          // If the alert is confirmed
          if (res) {
            // Get the instance of the data grid component
            var dataGrid = this.$refs["dx-data-grid-comp"].instance;

            // Save the edit data
            this.$nextTick(() => {
              dataGrid.saveEditData();
            });

            // After a delay, update the discount percentage and the data source
            setTimeout(() => {
              newData.TriggerDiscountUpdate = false;
              newData.DiscPcnt = currentRowData.DiscPcntPreviousValue;
              this.updateDataSource(newData, currentRowData, true);

              // After a delay, edit the discount percentage cell and focus on it
              setTimeout(() => {
                var rowIndex = dataGrid.getRowIndexByKey(currentRowData.ID);
                var cell = dataGrid.getCellElement(rowIndex, "DiscPcnt");
                dataGrid.editCell(rowIndex, "DiscPcnt");

                this.$nextTick(() => {
                  dataGrid.focus(cell);
                });
              }, 50);

              // After a delay, apply the focus class
              setTimeout(() => {
                this.applyFocusClass();
              }, 100);
            }, 100);
          }
        }

        // Return the new data
        return newData;
      } else {
        // Update the discount percentage and the sales UM net FX price
        newData.TriggerDiscountUpdate = false;
        newData.DiscPcnt = value;
        newData.DiscPcntPreviousValue = value;
        newData.SalesUMNetFXPrice = SalesUMNetFXPrice;

        // Merge the current row data with the new data
        var obj = { ...currentRowData, ...newData };
        // Set the stock prices and the line totals
        obj = SalesOrderState.setStockPrices(obj);
        obj = SalesOrderState.setLineTotals(obj, this.model);

        // If the sales UM quantity is greater than 0, add the object to the review
        if (obj.SalesUMQty > 0) {
          this.addToReview(obj);
        }

        // Update the data source
        this.updateDataSource(obj);

        // Return the object
        return obj;
      }
    },
    async updateThePriceDataOnUMChange(newData, currentRowData, obj) {
      // Call the setSaleItemPrice method from the SalesOrderV3Service
      // Pass in the new data, the object, and various properties from the model
      return SalesOrderV3Service.setSaleItemPrice(
        newData,
        obj,
        this.model.SalesOrderTypeCode,
        this.model.BranchCode,
        this.model.CustomerCode,
        this.model.ExchangeRate,
        this.model.DateIn,
        this.model.RequiredByDate,
        false
      ).then((res) => {
        // Merge the current row data with the result
        newData = { ...currentRowData, ...res };

        // If the sales UM quantity is greater than 0, add the new data to the review
        if (newData.SalesUMQty > 0) {
          this.addToReview(newData);
        }
      });
    },
    onProductSelected(e) {
      // Add the selected item to the selectedProducts array
      this.selectedProducts.push(e.selectedItem);
    },
    onEnterItemCode(e, lineData) {
      // If the key pressed is "Enter"
      if (e.event.key == "Enter") {
        // After a delay of 100 milliseconds
        setTimeout(() => {
          // If the current view is "GLList", focus on the "TaxCode" cell
          if (this.soDetailsCurrentView == "GLList")
            this.focusOnEditableCell(lineData.rowIndex, "TaxCode");
          // Otherwise, focus on the "SalesUMQty" cell
          else this.focusOnEditableCell(lineData.rowIndex, "SalesUMQty");
        }, 100);
      }
    },
    focusOnEditableCell(rowIndex, col) {
      // Set the column name to the provided column or "ItemCode" if no column is provided
      var columnName = col ? col : "ItemCode";
      // Get the instance of the data grid component
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;

      // Get the cell element at the provided row index and column name
      var cellElement = dataGrid.getCellElement(rowIndex, columnName);
      // Focus on the cell element
      dataGrid.focus(cellElement);
      // After a delay of 10 milliseconds, edit the cell
      setTimeout(() => {
        dataGrid.editCell(rowIndex, columnName);
      }, 10);
    },
    addNewRow() {
      // If the current view is "ProductList"
      if (this.soDetailsCurrentView == "ProductList") {
        // Create a new object from the base record
        let dataObj = Object.assign({ ...soDetailsBaseRecord });
        dataObj.LineNo = this.productDataSourceItems._array.length + 1;
        // Insert the new object into the product data source items
        this.productDataSourceItems.insert(dataObj);
      } else {
        // Create a new object from the base record
        let dataObj = Object.assign({ ...soDetailsBaseRecord });
        dataObj.LineNo = this.glListDataSourceItems._array.length + 1;
        // Insert the new object into the GL list data source items
        this.glListDataSourceItems.insert(dataObj);
      }

      // var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      // dataGrid.saveEditData();
    },
    async setTaxCodeDataSourceItems() {
      // Define the data source configuration for the tax code
      let taxCodeDS = TaxCategoryService.dataSourceConfig("api/Tax", {
        // Filter to only include items where "Inactive" is "N"
        filter: [["Inactive", "=", "N"]],
        // Sort by "Description" in descending order
        sort: [{ selector: "Code", asc: false }],
      });

      // Load the data source and assign it to the taxCodeDataSourceItems property
      this.taxCodeDataSourceItems = await taxCodeDS.load();
    },
    refreshDataSource() {
      // Get the instance of the data grid component
      var grid = this.$refs["dx-data-grid-comp"].instance;
      // Set the data source of the grid to an empty array
      grid.option("dataSource", []);
      // After a delay of 1 millisecond
      setTimeout(() => {
        // Set the data source of the grid to the getDataSource property
        grid.option("dataSource", this.getDataSource);
        // Refresh the grid
        grid.refresh();
      }, 1);
    },
    async triggerPriceChange(label) {
      // If there are items in the review data list
      if (this.reviewDataList._array.length > 0) {
        // If price change confirmation is allowed
        if (this.allowPriceChangeTriggerConfirmation) {
          // Ask the user if they want to recalculate the sell price of all items
          let confirmPriceChange = await this.$confirm(
            `Would you like to recalculate the sell price of all items based upon this new ${label}?`,
            "Price Change"
          );

          // If the user confirms, set the sale item price for all lines
          if (confirmPriceChange) {
            this.setSaleItemPriceAllLines();
          }
        } else {
          // If price change confirmation is not allowed, set the sale item price for all lines
          this.setSaleItemPriceAllLines();
        }
      }
    },
    setSaleItemPriceAllLines() {
      // If there are items in the review data list
      if (this.reviewDataList._array.length > 0) {
        var promises = [];
        // For each item in the review data list
        for (var i = 0; i < this.reviewDataList._array.length; i++) {
          var record = this.reviewDataList._array[i];
          // If the item has an item code and is not of type "G"
          if (record.ItemCode && record.ItemType != "G")
            // Add a promise to set the sale item price to the promises array
            promises.push(
              SalesOrderV3Service.setSaleItemPrice(
                // REPRICE ALL
                record,
                record,
                this.model.SalesOrderTypeCode,
                this.model.BranchCode,
                this.model.CustomerCode,
                this.model.ExchangeRate,
                this.model.DateIn,
                new Date(this.model.RequiredByDate),
                false
              )
            );
        }

        // When all promises have resolved
        Promise.all(promises).then((data) => {
          // For each element in the data
          data.forEach((element) => {
            // Update the data source with the element
            this.updateDataSource(element, undefined, true);
          });
        });
      }
    },
    addBatchItemsToCollections(item) {
      // Find the index of the item in allBatchData that has the same BatchNo and ProductCode as the provided item
      var index = this.allBatchData.findIndex(
        (x) => x.BatchNo == item.BatchNo && x.ProductCode == item.ProductCode
      );
      // If the item is not found, add it to allBatchData
      if (index == -1) this.allBatchData.push(item);
    },
    addToReview(item) {
      // Check if the item has an OriginItemCode or an ItemCode
      if (item.OriginItemCode || item.ItemCode) {
        // Check if the current view is not "Review"
        if (this.soDetailsCurrentView != "Review") {
          // Find the index of the item in reviewDataList._array that has the same ID as the provided item
          var i = this.reviewDataList._array.findIndex((x) => x.ID == item.ID);

          // If the item is not found and its SalesUMQty is not 0, insert it into reviewDataList
          if (i == -1 && item.SalesUMQty != 0) {
            this.reviewDataList.push([
              {
                type: "insert",
                key: item.ID,
                data: item,
              },
            ]);
          } else {
            // If the item's SalesUMQty is 0, remove it from reviewDataList and clear focusedCellData and selectedRowData
            if (item.SalesUMQty == 0) {
              this.reviewDataList.remove(item.ID);

              this.focusedCellData = undefined;
              this.selectedRowData = undefined;
            } else {
              // Otherwise, update the item in reviewDataList
              this.reviewDataList.push([
                {
                  type: "update",
                  data: item,
                  key: item.ID,
                },
              ]);
            }
            // Load reviewDataList
            this.reviewDataList.load();
          }
        } else {
          // If the current view is "Review" and the item's SalesUMQty is 0, remove it from reviewDataList and clear focusedCellData and selectedRowData
          if (item.SalesUMQty == 0) {
            this.reviewDataList.push([
              {
                type: "remove",
                key: item.ID,
              },
            ]);

            this.focusedCellData = undefined;
            this.selectedRowData = undefined;
          } else {
            // Otherwise, update the item in reviewDataList
            this.reviewDataList.push([
              {
                type: "update",
                data: item,
                key: item.ID,
              },
            ]);
          }
          // Load reviewDataList
          this.reviewDataList.load();
        }

        // After a delay of 200 milliseconds, set the footer items to the items in reviewDataList._array
        setTimeout(() => {
          this.setFooterItems(this.reviewDataList._array);
        }, 200);
      }
    },
    async onOrderNoChanged(e) {
      if (getFactorByCode(578).Factorc !== "Y" || e.value == "") return;
      let orderNo = e.value;

      let result = await SalesOrderService.findOrderNoUsageForSalesOrder(
        orderNo,
        this.model.Code,
        this.model.CustomerCode
      );

      let orderNoUsedPreviously = result.data.value.length > 0;
      if (!orderNoUsedPreviously) {
        result = await SalesOrderService.findOrderNoUsageForInvoice(
          orderNo,
          this.model.Code,
          this.model.CustomerCode
        );
        orderNoUsedPreviously = result.data.value.length > 0;
      }

      if (orderNoUsedPreviously) {
        // Show prompt
        let confirmOrderNoReuse = await this.$confirm(
          `Customer ${this.model.CustomerName} has used Customer PO ${orderNo} previously. Will you use Customer PO ${orderNo} again?`,
          "Customer PO Used Previously"
        );

        if (!confirmOrderNoReuse) {
          e.component.focus();
          e.element.querySelector("input").select();
        }
      }
    },
    removeParentGridCellFocus() {
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      dataGrid.clearSelection();
      dataGrid.option("focusedRowIndex", -1);
    },

    // End Create Invoice
    moveOnKeyDown(e) {
      if (e.event.keyCode == 9 && !e.event.shiftKey) {
        var dataGrid = this.$refs["dx-data-grid-comp"].instance;
        if (dataGrid.getDataSource().items().length > 0) {
          e.event.preventDefault();

          if (this.baseView.mode === "create") {
            var cellElement = dataGrid.getCellElement(0, "ItemCode");
            dataGrid.focus(cellElement);
            dataGrid.editCell(0, "ItemCode");
          }
        }
      }
    },
    onHeaderFormContentReady(e) {
      const headerForm = document.querySelectorAll(".gc-header-form");
      this.headerFormHeight = headerForm[0].clientHeight;

      this.headerDxFormHeight = e.element.offsetHeight;

      if (e && this.baseView.mode != "review" && this.loaded)
        setTimeout(() => {
          var headerToolBarForm = e.component;
          if (headerToolBarForm && headerToolBarForm.getEditor("DateIn"))
            headerToolBarForm.getEditor("DateIn").instance().focus();
        }, 500);
    },
    toolBarFormContentReady(e) {
      if (e && this.baseView.mode != "review" && this.loaded)
        setTimeout(() => {
          var headerToolBarForm = e.component;
          if (headerToolBarForm && headerToolBarForm.getEditor("CustomerCode"))
            headerToolBarForm.getEditor("CustomerCode").instance().focus();
        }, 500);
    },
    closeDeliveryAddressPopup() {
      this.editDeliveryAddressVisible = false;
    },
    showDeliveryAddress() {
      var item = { ...this.model };
      this.DeliveryAddressModel = {
        DeliveryAddressCompanyName: item.DeliveryAddressCompanyName,
        DeliveryAddressCountry: item.DeliveryAddressCountry,
        DeliveryAddressPostcode: item.DeliveryAddressPostcode,
        DeliveryAddressState: item.DeliveryAddressState,
        DeliveryAddressStreetLine1: item.DeliveryAddressStreetLine1,
        DeliveryAddressStreetLine2: item.DeliveryAddressStreetLine2,
        DeliveryAddressSuburb: item.DeliveryAddressSuburb,
      };
      this.editDeliveryAddressVisible = true;
    },
    updateDeliveryAddress() {
      this.editDeliveryAddressVisible = false;

      this.model.DeliveryAddressCompanyName =
        this.DeliveryAddressModel.DeliveryAddressCompanyName;
      this.model.DeliveryAddressStreetLine1 =
        this.DeliveryAddressModel.DeliveryAddressStreetLine1;
      this.model.DeliveryAddressStreetLine2 =
        this.DeliveryAddressModel.DeliveryAddressStreetLine2;
      this.model.DeliveryAddressSuburb =
        this.DeliveryAddressModel.DeliveryAddressSuburb;
      this.model.DeliveryAddressState =
        this.DeliveryAddressModel.DeliveryAddressState;
      this.model.DeliveryAddressPostcode =
        this.DeliveryAddressModel.DeliveryAddressPostcode;
      this.model.DeliveryAddressCountry =
        this.DeliveryAddressModel.DeliveryAddressCountry;
    },
    headerFormValueChanged(isValid, brokenRules) {
      this.isValid = false;
      if (!isValid) this.displayValidationSummary(brokenRules);
      // else {
      // }
    },
    popupHiddenCallBack(show) {
      this.popupVisible = show;
    },
    async checkHeaderFormValidation() {
      let headerform = await validationEngine.validateGroup("gc-header-form");
      let commentsform = await validationEngine.validateGroup(
        "gc-header-comments"
      );

      if (!headerform.isValid || !commentsform.isValid)
        this.displayValidationSummary(
          headerform.brokenRules.concat(commentsform.brokenRules)
        );
      else {
        // Save as sales order for create invoice
        if (this.baseView.mode == "create") {
          if (this.reviewDataList._array.length == 0) {
            this.displayValidationErrors([
              "Invoice has no detail. Please add one or more lines before attempting to save the transaction again.",
            ]);
            return;
          }
          let payload = this.getSalesOrderDespatchInvoicePayload();

          this.showLoading(true);
          await InvoiceService.saveCreateInvoice(payload)
            .then((res) => {
              if (res) this.showLoading(false);
              this.$notify(`Invoice successfully created`, "success");
              this.setIgnoreRouteChangeConfirmation();
              if (res) {
                this.$router.push({
                  path: `/invoice/review/${res.data.Code}`,
                });
              }
              // if (this.baseView.query && this.baseView.query.from)
              //   this.$router.push({
              //     path: this.baseView.query.from,
              //   });
              else
                this.$router.push({
                  path: "/invoiced-order",
                });
            })
            .catch((err) => {
              this.showLoading(false);
              this.displayServerError(err, "Invoice Save Failed");
            });
        } else {
          let payload = this.getSavePayload();
          var confirmSave = true;

          const zeroDollarItems = this.getInvoiceDetailsPayload().filter(
            (i) => i.SalesUMFXPrice == 0
          );
          if (zeroDollarItems.length > 0) {
            confirmSave = await this.$confirm(
              "There are entries with prices set at $0.00. Do you wish to continue?",
              "Edit Invoice"
            );
          }

          if (confirmSave) {
            this.showLoading(true);
            await InvoiceService.saveEditInvoice(payload)
              .then((res) => {
                this.showLoading(false);
                if (this.baseView.mode == "create") {
                  this.model.Code = res.data.Code;
                }
                let new_invoice_was_created =
                  res.data.Code != res.data.OriginalInvoiceCode;
                let message_non_financial_impact = `Invoice # ${this.model.Code} successfully updated`;
                let message_financial_impact = `Invoice # ${res.data.OriginalInvoiceCode} has been credited and Invoice # ${res.data.Code} has been successfully created`;

                let success_message = new_invoice_was_created
                  ? message_financial_impact
                  : message_non_financial_impact;
                this.$notify(success_message, "success");
                this.setIgnoreRouteChangeConfirmation();
                if (this.baseView.query && this.baseView.query.from)
                  this.$router.push({
                    path: this.baseView.query.from,
                  });
                else
                  this.$router.push({
                    path: "/invoiced-order",
                  });
              })
              .catch((error) => {
                this.showLoading(false);
                this.displayServerError(error, "Invoice Update Failed");
              });
          }
        }
      }
    },
    getSavePayload() {
      var ExTaxTotal = 0;
      var TaxTotal = 0;
      var Total = 0;
      this.localDataSource._array.forEach((element) => {
        if (element.ItemCode && element.SalesUMQty) {
          ExTaxTotal = ExTaxTotal + element.TaxableFXLineTotal;
          TaxTotal = TaxTotal + element.TaxFXLineTotal;
          Total = Total + element.FXLineTotal;
        }
      });

      var payload = {
        BranchCode: this.model.BranchCode,
        Code: this.model.Code,
        CommissionPcnt: this.model.AgentCommissionPcnt,
        ConsignmentNo: this.model.ConsignmentNo,
        CurrencyCode: this.model.CurrencyCode,
        CustomerCode: this.model.CustomerCode,
        CustomerDeliveryAddressLineNo: 1,
        DateDue: this.model.DateDue,
        DateIn: this.model.DateIn,
        DeliveryAddressCompanyName: this.model.DeliveryAddressCompanyName,
        DeliveryAddressCountry: this.model.DeliveryAddressCountry,
        DeliveryAddressPostcode: this.model.DeliveryAddressPostcode,
        DeliveryAddressState: this.model.DeliveryAddressState,
        DeliveryAddressStreetLine1: this.model.DeliveryAddressStreetLine1,
        DeliveryAddressStreetLine2: this.model.DeliveryAddressStreetLine2,
        DeliveryAddressSuburb: this.model.DeliveryAddressSuburb,
        DivisionCode: this.model.DivisionCode,
        ExchangeRate: this.model.ExchangeRate,
        InvoiceDetails: this.getInvoiceDetailsPayload(),
        OrderNo: this.model.OrderNo,
        OriginalInvoiceCode: this.model.Code,
        SalesOrderCode: this.model.SalesOrderCode,
        SalesOrderDateChanged: this.model.SalesOrderDateChanged,
        SalesOrderTypeCode: this.model.SalesOrderTypeCode,
        SalesRepresentativeCode: this.model.SalesRepresentativeCode,
        ExTaxTotal: parseFloat(ExTaxTotal.toFixed(2)),
        TaxTotal: parseFloat(TaxTotal.toFixed(2)),
        Total: parseFloat(Total.toFixed(2)),
        UserComment1: this.commentModel.UserComment1,
        UserComment2: this.commentModel.UserComment2,
        UserComment3: this.commentModel.UserComment3,
        UserComment4: this.commentModel.UserComment4,
        UserComment5: this.commentModel.UserComment5,
        UserComment6: this.commentModel.UserComment6,
        UserComment7: this.commentModel.UserComment7,
        UserComment8: this.commentModel.UserComment8,
        UserComment9: this.commentModel.UserComment9,
        UserComment10: this.commentModel.UserComment10,
        UserComment11: this.commentModel.UserComment11,
        UserComment12: this.commentModel.UserComment12,
        UserComment13: this.commentModel.UserComment13,
        UserComment14: this.commentModel.UserComment14,
        UserComment15: this.commentModel.UserComment15,
        UserComment16: this.commentModel.UserComment16,
        UserComment17: this.commentModel.UserComment17,
        UserComment18: this.commentModel.UserComment18,
        UserComment19: this.commentModel.UserComment19,
        UserComment20: this.commentModel.UserComment20,
      };

      return payload;
    },
    getSalesOrderDespatchInvoicePayload() {
      var ExTaxTotal = 0;
      var TaxTotal = 0;
      var Total = 0;
      this.reviewDataList._array.forEach((element) => {
        if (element.ItemCode && element.SalesUMQty) {
          ExTaxTotal = ExTaxTotal + element.TaxableFXLineTotal;
          TaxTotal = TaxTotal + element.TaxFXLineTotal;
          Total = Total + element.FXLineTotal;
        }
      });
      let payload = {
        IncludesGeneralLedgerLines: this.reviewDataList._array.find(
          (x) => x.ItemType == "G"
        )
          ? "Y"
          : "N",
        BranchCode: this.model.BranchCode,
        Code: 0,
        CompanyCode: this.model.CompanyCode,
        ConsignmentNo: this.model.ConsignmentNo,
        CurrencyCode: this.model.CurrencyCode,
        CustomerCode: this.model.CustomerCode,
        CustomerDeliveryAddressLineNo:
          this.model.CustomerDeliveryAddressLineNo.toString(),
        DateIn: new Date(this.model.DateIn).toSaveDate(),
        DeliveryAddressCompanyName: this.model.DeliveryAddressCompanyName,
        DeliveryAddressCountry: this.model.DeliveryAddressCountry,
        DeliveryAddressPostcode: this.model.DeliveryAddressPostcode,
        DeliveryAddressState: this.model.DeliveryAddressState,
        DeliveryAddressStreetLine1: this.model.DeliveryAddressStreetLine1,
        DeliveryAddressStreetLine2: this.model.DeliveryAddressStreetLine2,
        DeliveryAddressSuburb: this.model.DeliveryAddressSuburb,
        Status: "N",
        DivisionCode: this.model.DivisionCode,
        ExchangeRate: this.model.ExchangeRate,
        ExTaxTotal: parseFloat(ExTaxTotal.toFixed(2)),
        ItemCount: 0,
        OrderNo: this.model.OrderNo,
        RequiredByDate: new Date(this.model.DateDue).toSaveDate(),
        SalesOrderTypeCode: this.model.SalesOrderTypeCode,
        SalesRepresentativeCode: this.model.SalesRepresentativeCode,
        ShipDate: new Date(this.model.DateDue).toSaveDate(),
        TaxTotal: parseFloat(TaxTotal.toFixed(2)),
        Total: parseFloat(Total.toFixed(2)),
        UserComment1: this.commentModel.UserComment1,
        UserComment2: this.commentModel.UserComment2,
        UserComment3: this.commentModel.UserComment3,
        UserComment4: this.commentModel.UserComment4,
        UserComment5: this.commentModel.UserComment5,
        UserComment6: this.commentModel.UserComment6,
        UserComment7: this.commentModel.UserComment7,
        UserComment8: this.commentModel.UserComment8,
        UserComment9: this.commentModel.UserComment9,
        UserComment10: this.commentModel.UserComment10,
        UserComment11: this.commentModel.UserComment11,
        UserComment12: this.commentModel.UserComment12,
        UserComment13: this.commentModel.UserComment13,
        UserComment14: this.commentModel.UserComment14,
        UserComment15: this.commentModel.UserComment15,
        UserComment16: this.commentModel.UserComment16,
        UserComment17: this.commentModel.UserComment17,
        UserComment18: this.commentModel.UserComment18,
        UserComment19: this.commentModel.UserComment19,
        UserComment20: this.commentModel.UserComment20,

        CommissionPcnt: this.model.AgentCommissionPcnt,
        SalesOrderDetails: this.getSalesOrderDetailPayload(),
      };

      payload.ItemCount = payload.SalesOrderDetails.length;

      return payload;
    },
    getSalesOrderDetailPayload() {
      let details = [];

      this.reviewDataList._array.forEach((data) => {
        let detailObj = {
          SalesOrderCode: 0,
          LineNo: 0,
          ItemCode: data.ItemCode,
          SalesUM: data.SalesUM,
          SalesUMQty: data.SalesUMQty,
          DiscPcnt: data.DiscPcnt,
          SalesUMFXPrice: data.SalesUMFXPrice,
          Comment: data.Comment,
          IsCommentVisible: data.IsCommentVisible,
          RecordId: "00000000-0000-0000-0000-000000000000",
          CustomerSalesContractCode: data.CustomerSalesContractCode,
          CustomerSalesContractLineNo: data.CustomerSalesContractLineNo,
          ProductBatches: [],
          ItemType: data.ItemType,
          TaxCode: data.TaxCode,
        };

        if (
          data.IsBatchProduct != "No" &&
          data.InvoiceProductBatches.length > 0
        ) {
          detailObj.ProductBatches = this.getInvoiceProductBatches(data);
        }

        if (data.SalesUMQty) details.push(detailObj);
      });

      return details;
    },
    getInvoiceProductBatches(data) {
      var batch = [];
      data.InvoiceProductBatches.forEach((element) => {
        if (element.BatchNo && element.SalesUMBatchQty != 0)
          batch.push({
            BatchNo: element.BatchNo,
            SalesUMBatchQty: element.SalesUMBatchQty,
            SalesUMInvoiceConversion: data.SalesUMConversion,
            StockUMBatchQty: calculateStockUMQty(
              element.SalesUMBatchQty,
              data.SalesUMConversion
            ),
            SupplierCode: element.SupplierCode,
          });
      });

      return batch;
    },
    getInvoiceDetailsPayload() {
      let details = [];

      this.localDataSource._array.forEach((data) => {
        let detailObj = {
          AddDiscPriceGroupCode: data.AddDiscPriceGroupCode,
          AddDiscPricingMethod: data.AddDiscPricingMethod,
          AddDiscPricingMethodValue: data.AddDiscPricingMethodValue,
          AddDiscSalesUMPrice: data.AddDiscSalesUMPrice,
          AddDiscStockUMPrice: data.AddDiscStockUMPrice,
          AlmondcoWriteDespatchData: this.getAlmondcoWriteDespatchData(data),
          CalculatedDiscPcnt: data.CalculatedDiscPcnt,
          Comment: data.Comment,
          CustomerProduct: data.CustomerProduct,
          DiscFXLineTotal: data.DiscFXLineTotal,
          DiscLineTotal: data.DiscLineTotal,
          DiscPcnt: data.DiscPcnt,
          FXLineTotal: data.FXLineTotal,
          InvoiceProductBatches: this.getInvoiceProductBatchesPayload(data),
          IsCommentVisible: data.IsCommentVisible,
          IsPrimeCostItem: data.IsPrimeCostItem,
          ItemCode: data.ItemCode,
          ItemType: data.ItemType,
          LineTotal: data.LineTotal,
          NetFXLineTotal: data.NetFXLineTotal,
          NetLineTotal: data.NetLineTotal,
          OriginalInvoiceLineNo: data.OriginalInvoiceLineNo,
          PriceGroupSalesUMPrice: data.PriceGroupSalesUMPrice,
          PriceGroupStockUMPrice: data.PriceGroupStockUMPrice,
          PricingMethod: data.PricingMethod,
          PricingMethodValue: data.PricingMethodValue,
          SalesUM: data.SalesUM,
          SalesUMConversion: data.SalesUMConversion,
          SalesUMCost: data.SalesUMCost,
          SalesUMFXPrice: data.SalesUMFXPrice,
          SalesUMInvoiceConversion: data.SalesUMInvoiceConversion,
          SalesUMIsVariable: data.SalesUMIsVariable,
          SalesUMManufacturedCost: data.SalesUMManufacturedCost,
          SalesUMNetFXPrice: data.SalesUMNetFXPrice,
          SalesUMNetPrice: data.SalesUMNetPrice,
          SalesUMPrice: data.SalesUMPrice,
          SalesUMPromotionNetPrice: data.SalesUMPromotionNetPrice,
          SalesUMQty: data.SalesUMQty,
          SalesUMStandardCost: data.SalesUMStandardCost,
          SalesUMStandardPrice: data.SalesUMStandardPrice,
          StockUM: data.StockUM,
          StockUMFXPrice: data.StockUMFXPrice,
          StockUMNetPrice: data.StockUMNetPrice,
          StockUMPrice: data.StockUMPrice,
          StockUMQty: data.StockUMQty,
          StockUMStandardPrice: data.StockUMStandardPrice,
          TaxCode: data.TaxCode,
          TaxFXLineTotal: data.TaxFXLineTotal,
          TaxLineTotal: data.TaxLineTotal,
          TaxRate: data.TaxRate,
          TaxableFXLineTotal: data.TaxableFXLineTotal,
          TaxableLineTotal: data.TaxableLineTotal,
          WetFXFreightAdjustment: data.WetFXFreightAdjustment,
          WetFXLineTotal: data.WetFXLineTotal,
          WetFreightAdjustment: data.WetFreightAdjustment,
          WetLineTotal: data.WetLineTotal,
          WetPcnt: data.WetPcnt,
          WriteOff: data.WriteOff,
        };

        details.push(detailObj);
      });

      return details;
    },
    getAlmondcoWriteDespatchData(data) {
      var despatchData = {};

      if (
        FeatureStorage.checkPermission("AlmondcoDespatchData", "create") &&
        this.almondcoQCData &&
        data.DespatchData
      ) {
        despatchData = {
          ItemCode: data.ItemCode,
          ItemDescription: data.ItemDescription,
          RecordId: data.RecordId,
          AlmondcoWriteDespatchDataDetails:
            this.getAlmondcoWriteDespatchDataDetails(data),
        };
      }

      return despatchData;
    },
    getAlmondcoWriteDespatchDataDetails(data) {
      var dataDetails = [];

      data.InvoiceProductBatches.forEach((element) => {
        var data = {
          DespatchDate: element.DespatchDate,
          PackedOnDate: element.PackedOnDate,
          LotNo: element.LotNo,
          PalletNo: element.PalletNo,
          WorkOrderNo: element.WorkOrder,
          CartonWeight: element.CartonWeight,
          CartonQuantity: element.CartonQty,
          TotalWeight: element.TotalWeight,
          RecordId: element.RecordID,
          DeliveryNote: element.DeliveryNote,
          DeliveryNoteLineNo: element.DeliveryNoteLineNo,
        };

        dataDetails.push(data);
      });

      return dataDetails;
    },
    getInvoiceProductBatchesPayload(data) {
      var batchDetails = [];

      if (
        FeatureStorage.checkPermission("AlmondcoDespatchData", "create") &&
        this.almondcoQCData
      ) {
        return batchDetails;
      } else {
        data.InvoiceProductBatches.forEach((element) => {
          var payload = {
            BatchNo: element.BatchNo,
            SalesUMBatchQty: element.SalesUMBatchQty,
            SalesUMInvoiceConversion: data.SalesUMInvoiceConversion,
            StockUMBatchQty: calculateStockUMQty(
              element.SalesUMBatchQty,
              data.SalesUMInvoiceConversion
            ),
            SupplierCode: element.SupplierCode,
          };

          batchDetails.push(payload);
        });

        return batchDetails;
      }
    },

    backButtonFunction() {
      this.$router
        .push({
          path: this.backButtonRoute,
        })
        .then(() => {
          this.$store.dispatch("dataContext/setDataViewType", undefined);
        });
    },
    async initialized() {
      if (this.baseView.mode == "edit") {
        await this.$store.dispatch("screenFactors/getFactors", [18, 571]);
        await InvoiceService.getInvoice(this.baseView.id, {
          select: [
            "Code",
            "CustomerCode",
            "CustomerName",
            "DateIn",
            "DateDue",
            "OrderNo",
            "AgentName",
            "AgentCommissionPcnt",
            "CustomerDeliveryAddressLineNo",
            "DeliveryAddressCompanyName",
            "DeliveryAddressCountry",
            "DeliveryAddressPostcode",
            "DeliveryAddressState",
            "DeliveryAddressStreetLine1",
            "DeliveryAddressStreetLine2",
            "DeliveryAddressSuburb",
            "BranchCode",
            "BranchDescription",
            "DivisionCode",
            "DivisionDescription",
            "SalesOrderTypeCode",
            "SalesRepresentativeCode",
            "ExchangeRate",
            "CurrencyCode",
            "UserComment1",
            "UserComment2",
            "UserComment3",
            "UserComment4",
            "UserComment5",
            "UserComment6",
            "UserComment7",
            "UserComment8",
            "UserComment9",
            "UserComment10",
            "UserComment11",
            "UserComment12",
            "UserComment13",
            "UserComment14",
            "UserComment15",
            "UserComment16",
            "UserComment17",
            "UserComment18",
            "UserComment19",
            "UserComment20",
            "DeliveryNoteCode",
            "SalesOrderCode",
          ],
        }).then((res) => {
          if (res.data.value.length > 0) {
            this.model = { ...this.model, ...res.data.value[0] };
            for (const key in this.model) {
              if (key.match(/Comment/))
                this.commentModel[key] = this.model[key];
            }

            this.model.BranchDescription = this.model.BranchCode + " - " + this.model.BranchDescription;
            this.model.DivisionDescription = this.model.DivisionCode + " - " + this.model.DivisionDescription;
          }
        });

        if (this.model.SalesOrderCode)
          await InvoiceService.getCustomRequest({
            url: "api/SalesOrder",
            filter: `Code eq ${this.model.SalesOrderCode}`,
            select: ["DateChanged", "ChangedBy", "Code"],
          }).then((res) => {
            if (res.data.value.length > 0) {
              this.model.SalesOrderDateChanged = res.data.value[0].DateChanged;
            }
          });

        if (FeatureStorage.checkPermission("AlmondcoDespatchData", "create")) {
          await AlmondcoDespatchDataService.checkQCData(
            this.model.DeliveryNoteCode
          )
            .then((res) => {
              if (res.data.value.length > 0) {
                this.almondcoQCData = res.data.value;
              } else {
                this.almondcoQCData = [];
              }
            })
            .catch((err) => {
              if (err) this.$logger.debug("error", err);
            });
        }

        var batchProdCodes = [];
        await InvoiceService.getInvoiceDetails(this.baseView.id, {
          select: [
            "InvoiceCode",
            "LineNo",
            "SalesUMQty",
            "IsBatchProduct",
            "ItemCode",
            "SalesUMInvoiceConversion",
            "PackSize",
            "SalesUMConversion",
            "ItemDescription",
            "TaxCode",
            "TaxRate",
            "SalesUM",
            "SalesUMOrderQty",
            "SalesUMNetFXPrice",
            "TaxableFXLineTotal",
            "TaxFXLineTotal",
            "FXLineTotal",
            "ItemType",
            "Comment",
            "SalesUMCreditedQty",
            "SalesUMFXPrice",
            "WetPcnt",
            "SalesTaxPcnt",
            "SalesUMConversion",
            "SalesUMPrice",
            "SalesUMNetPrice",
            "DiscPcnt",
            "DiscValue",
            "SalesUMStandardPrice",
            "IsCommentVisible",
          ],
        }).then((res) => {
          res.data.value.forEach(async (element) => {
            element.OriginalSalesUMQty = element.SalesUMQty;
            element.OriginalInvoiceLineNo = element.LineNo;
            element["InvoiceProductBatches"] = [];
            if (element["IsBatchProduct"] != "No") {
              batchProdCodes.push(element.ItemCode);
              await InvoiceCreditService.getProductBatches(
                `InvoiceCode eq ${element["InvoiceCode"]} and InvoiceLineNo eq ${element["LineNo"]}`,
                {
                  select: [
                    "BatchNo",
                    "ItemCode",
                    "StockUMQty",
                    "SalesUMQty",
                    "SupplierCode",
                    "SupplierName",
                    "InvoiceLineNo",
                    "InvoiceCode",
                    "SalesUMCreditedQty",
                    "UseByDate",
                    "AvailableQty",
                  ],
                }
              ).then((response) => {
                element["InvoiceProductBatches"] = response.data.value.map(
                  (el, index) => {
                    let batchLine = {
                      BatchNo: el.BatchNo,
                      ItemCode: el.ItemCode,
                      StockUMBatchQty: el.StockUMQty,
                      SalesUMBatchQty: el.SalesUMQty,
                      SalesUMInvoiceConversion:
                        element.SalesUMInvoiceConversion,
                      SupplierCode: el.SupplierCode,
                      SupplierName: el.SupplierName,
                      InvoiceLineNo: el.InvoiceLineNo,
                      SalesUMCreditedQty: el.SalesUMCreditedQty,
                      UseByDate: el.UseByDate,
                      BatchLineNo: index,
                      AvailableQty: el.AvailableQty,
                      AllocatedQty: 0,
                    };
                    return batchLine;
                  }
                );
              });
            }

            if (
              FeatureStorage.checkPermission("AlmondcoDespatchData", "create")
            ) {
              if (this.almondcoQCData) {
                element["InvoiceProductBatches"] = this.almondcoQCData.filter(
                  (x) => x.InvoiceLineNo == element.LineNo
                );

                if (element["InvoiceProductBatches"].length > 0) {
                  element.IsBatchProduct = "Yes";
                  element.DespatchData = true;
                  element["InvoiceProductBatches"].forEach((item) => {
                    item.PackSize = element.PackSize;
                    item.SalesUMConversion = element.SalesUMConversion;
                  });
                } else {
                  element.IsBatchProduct = "Yes";
                  element.DespatchData = false;
                  element.InvoiceProductBatches.push({
                    CartonOutstandingQty: 0,
                    CartonQty: 0,
                    CartonWeight: 0,
                    ChangedBy: undefined,
                    Code: undefined,
                    CreatedBy: undefined,
                    DateChanged: undefined,
                    DateCreated: undefined,
                    DespatchDate: new Date(),
                    InvoiceLineNo: element.LineNo,
                    LotNo: undefined,
                    PackedOnDate: undefined,
                    PalletNo: undefined,
                    RecordID: undefined,
                    TotalWeight: 0,
                    WorkOrder: undefined,
                    PackSize: element.PackSize,
                    SalesUMConversion: element.SalesUMConversion,
                  });
                }
              }
            }

            await InvoiceService.getCustomDataSocurce("ProductUnitOfMeasure", {
              filter: `ProductCode eq '${element.ItemCode}' and BranchCode eq '${this.model.BranchCode}'`,
              select: ["ProductCode", "BranchCode", "UnitOfMeasureCode"],
            }).then((res) => {
              element.SalesUMs = res.data.value;
            });

            element = SalesOrderState.setStockPrices(element);
            element = SalesOrderState.setLineTotals(element, this.model);
            this.localDataSource.insert(element);
          });
        });

        if (batchProdCodes.length != 0) {
          await this.getBatchItems(batchProdCodes);
        }
        this.$store.dispatch(
          "defaultCustomerSettings/setCustomerById",
          this.model.CustomerCode
        );
        await this.getCompanyData();
        await this.getCurrencyData();

        await this.setOrderComments(this.model.SalesOrderTypeCode);

        this.setFooterItems(this.localDataSource._array);

        this.loaded = true;
        this.showLoading(false);
      } else {
        if (this.baseView.mode == "create") {
          await SettingsService.getBySettingByApplicationFeatureCode(
            "SalesOrder"
          ).then((res) => {
            res.data.value.forEach((element) => {
              if (element.SettingName == "PriceChangeTriggerConfirmation") {
                if (element.ScopeCode == "Global") {
                  if (element.SettingValue == "Y")
                    this.allowPriceChangeTriggerConfirmation = true;
                }
              }
            });
          });
          await this.$store.dispatch(
            "screenFactors/getFactors",
            [18, 65, 66, 87, 95, 571, 578]
          );
          this.setTaxCodeDataSourceItems();
          // await this.getCompanyData();
          await this.setDefaultSalesRep();

          this.model.SalesOrderTypeCode = getFactorByCode(87).Factorc;
          if (this.model.SalesOrderTypeCode)
            await this.setOrderComments(this.model.SalesOrderTypeCode);

          if (this.baseView.id) {
            this.model.CustomerCode = this.baseView.id;
            // this.soDetailsCurrentView = "CustomerList";
          } else {
            this.soDetailsCurrentView = "Review";
          }

          let response = await DefaultCompanyBranchDivisionService.getDefault();

          if (response.data.value.length > 0) {
            if (!this.model.CompanyCode)
              this.model.CompanyCode = response.data.value[0].CompanyCode;
            if (!this.model.BranchCode)
              this.model.BranchCode = response.data.value[0].BranchCode;
            if (!this.model.DivisionCode)
              this.model.DivisionCode = response.data.value[0].DivisionCode;
          }

          if (!this.model.CompanyCode) {
            this.model.CompanyCode = getFactorByCode(18).Factorc;
          }

          if (!this.model.BranchCode) {
            this.model.BranchCode = getFactorByCode(65).Factorc;
          }

          if (!this.model.DivisionCode) {
            this.model.DivisionCode = getFactorByCode(66).Factorc;
          }
          await this.getSalesUMs(this.model.BranchCode);

          await this.getCurrencyData();

          var dataObj = { ...soDetailsBaseRecord };
          dataObj.LineNo = 1;
          this.productDataSourceItems.insert(dataObj);
          this.glListDataSourceItems.insert(dataObj);

          await InvoiceService.getActiveAccountingPeriodStartDate().then(
            (res) => {
              if (res.data.value.length > 0) {
                this.DateIn.min = new Date(res.data.value[0].StartDate);
                if (new Date() < this.DateIn.min)
                  this.model.DateIn = this.DateIn.min;
              }
            }
          );
          await InvoiceService.getActiveAccountingPeriodEndDate().then(
            (res) => {
              if (res.data.value.length > 0) {
                this.DateIn.max = new Date(res.data.value[0].EndDate);
                if (new Date() > this.DateIn.max)
                  this.model.DateIn = this.DateIn.max;
              }
            }
          );
        }
        this.loaded = true;
        this.showLoading(false);
      }
    },

    async getCompanyData() {
      this.model.CompanyCode = getFactorByCode(18).Factorc;

      let response = undefined;
      if (!this.model.CompanyCode) {
        response = await DefaultCompanyBranchDivisionService.getDefault();

        if (response.data.value.length > 0) {
          this.model.CompanyCode = response.data.value[0].CompanyCode;
        }
      }
    },

    async getCurrencyData() {
      await CompanyService.getByCompanyCode(this.model.CompanyCode, {
        select: ["CurrencyCode", "Code"],
      }).then((res) => {
        if (res.data.value.length > 0) {
          if (!this.model.CurrencyCode)
            this.model.CurrencyCode = res.data.value[0].CurrencyCode;
          this.model.CompanyCurrency = res.data.value[0].CurrencyCode;
        }
      });
    },

    async getBatchItems(batchProdCodes) {
      var filter = undefined;
      batchProdCodes.forEach((element) => {
        if (!filter) filter = `ItemCode eq '${element}'`;
        else filter = filter + ` or ItemCode eq '${element}'`;
      });

      await ProductBatchService.getAllBatchNo(filter).then((res) => {
        res.data.value.forEach((element) => {
          this.addBatchItemsToCollections(element);
        });
      });
    },

    // Header Form
    async onSalesOrderTypeRowClick(e) {
      this.showLoading(true);
      this.model.SalesOrderTypeCode = e.itemData.Code;
      this.model.SalesOrderTypeDescription = e.itemData.Description;
      await this.setOrderComments(this.model.SalesOrderTypeCode);

      this.showLoading(false);
    },
    onExchangeValueChanged(e) {
      if (!e.event) return;
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      dataGrid.option("loadPanel.enabled", false);

      if (e.value <= 0) {
        this.displayValidationSummary([
          {
            message: "Exchange rate should be greater than 0.0000",
          },
        ]);
        this.model.ExchangeRate = e.previousValue;
      } else {
        this.triggerPriceChange("Exchange Rate");
      }
    },
    onAgentCommissionPcntValueChanged(e) {
      this.model.AgentCommissionPcnt = e.value;
      this.setFooterItems(this.localDataSource._array);
    },

    // Details Tab
    async onPriceUpdateValidateGPToolPrcnt(newData, value, currentRowData) {
      // -----
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      dataGrid.option("loadPanel.enabled", false);

      var SalesUMNetFXPrice = calculateSalesUMNetFXPrice(
        value,
        currentRowData.DiscPcnt
      );

      let newsalesUMNetPrice = calculateSalesUMNetPriceFromSalesUMNetFXPrice(
        SalesUMNetFXPrice,
        this.model.ExchangeRate
      );

      let tempGp = calculateGrossProfitPcnt(
        currentRowData.SalesUMStandardCost,
        newsalesUMNetPrice
      );

      let globalGPVal = getFactorByCode(571).FactorInt;

      if (globalGPVal != 0 && tempGp < globalGPVal) {
        if (currentRowData.SalesUMQty != 0) {
          let res = await alert(
            `Net Price is below the minimum GP of ${
              globalGPVal.roundNumber(2) + " %"
            }. Please enter a discount that meets the minimum GP % requirement.`,
            "Validation Error"
          );

          if (res) {
            setTimeout(() => {
              setTimeout(() => {
                var rowIndex = dataGrid.getRowIndexByKey(currentRowData.LineNo);
                var cell = dataGrid.getCellElement(rowIndex, "SalesUMFXPrice");
                dataGrid.editCell(rowIndex, "SalesUMFXPrice");

                this.$nextTick(() => {
                  dataGrid.focus(cell);
                });
              }, 50);
            }, 100);
          }
        }
      } else {
        newData.SalesUMFXPrice = value;
        newData.SalesUMFXPricePreviousValue = value;

        newData.SalesUMNetFXPrice = SalesUMNetFXPrice;

        // setBaseCurrencyPriceFromFXPrice
        newData.SalesUMPrice = calculateSalesUMPriceFromSalesUMFXPrice(
          newData.SalesUMFXPrice,
          this.model.ExchangeRate
        );

        newData.CalculatedDiscPcnt = calculateCalculatedDiscPcnt(
          currentRowData.SalesUMStandardPrice,
          newData.SalesUMPrice
        );

        newData.SalesUMNetPrice = newsalesUMNetPrice;

        // setStockPrices
        newData.StockUMQty = calculateStockUMQty(
          currentRowData.SalesUMQty,
          currentRowData.SalesUMConversion
        );

        // Stock
        newData.StockUMFXPrice = calculateStockUMFXPrice(
          newData.SalesUMFXPrice,
          currentRowData.SalesUMConversion
        );

        newData.StockUMNetFXPrice = calculateStockUMNetFXPrice(
          newData.SalesUMNetFXPrice,
          currentRowData.SalesUMConversion
        );

        newData.StockUMPrice = calculateStockUMPrice(
          newData.SalesUMPrice,
          currentRowData.SalesUMConversion
        );

        newData.StockUMNetPrice = calculateStockUMNetPrice(
          newData.SalesUMNetPrice,
          currentRowData.SalesUMConversion
        );

        // setLineTotals
        // FX Totals
        newData.DiscFXLineTotal = calculateDiscFXLineTotal(
          newData.SalesUMFXPrice,
          newData.SalesUMNetFXPrice,
          currentRowData.SalesUMQty
        );
        newData.NetFXLineTotal = calculateNetFXLineTotal(
          newData.SalesUMNetFXPrice,
          currentRowData.SalesUMQty,
          newData.SalesUMFXPrice
        );
        newData.WetFXLineTotal = calculateWetFXLineTotal(
          newData.NetFXLineTotal,
          currentRowData.WetPcnt
        );

        newData.TaxableFXLineTotal = calculateTaxableFXLineTotal(
          newData.NetFXLineTotal,
          newData.WetFXLineTotal
        );
        newData.TaxFXLineTotal = calculateTaxFXLineTotal(
          newData.NetFXLineTotal,
          newData.WetFXLineTotal,
          currentRowData.SalesTaxPcnt
        );
        newData.FXLineTotal = calculateFXLineTotal(
          newData.TaxableFXLineTotal,
          newData.TaxFXLineTotal
        );

        // Totals
        newData.DiscLineTotal = calculateDiscLineTotal(
          newData.DiscFXLineTotal,
          this.model.ExchangeRate
        );
        newData.NetLineTotal = calculateNetLineTotal(
          newData.NetFXLineTotal,
          this.model.ExchangeRate
        );
        newData.WetLineTotal = calculateWetLineTotal(
          newData.WetFXLineTotal,
          this.model.ExchangeRate
        );
        newData.TaxableLineTotal = calculateTaxableLineTotal(
          newData.TaxableFXLineTotal,
          this.model.ExchangeRate
        );
        newData.TaxLineTotal = calculateTaxLineTotal(
          newData.TaxFXLineTotal,
          this.model.ExchangeRate
        );
        newData.LineTotal = calculateLineTotal(
          newData.FXLineTotal,
          this.model.ExchangeRate
        );

        newData.TriggerPriceUpdate = false;

        newData = { ...currentRowData, ...newData };
      }

      return newData;
    },
    async updatePriceLookup(newData, value, currentRowData) {
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      // dataGrid.saveEditData();
      dataGrid.option("loadPanel.enabled", false);
      var obj = { ...currentRowData, ...newData };

      return SalesOrderV3Service.setSaleItemPrice(
        newData,
        obj,
        this.model.SalesOrderTypeCode,
        this.model.BranchCode,
        this.model.CustomerCode,
        this.model.ExchangeRate,
        this.model.DateIn,
        this.model.RequiredByDate,
        false
      ).then((res) => {
        newData = { ...currentRowData, ...res };

        this.localDataSource.update(currentRowData.LineNo, newData).then(() => {
          dataGrid.saveEditData();
          this.setFooterItems(this.localDataSource._array);
          var rowIndex = dataGrid.getRowIndexByKey(currentRowData.LineNo);
          dataGrid.repaintRows([rowIndex]);

          setTimeout(() => {
            dataGrid.refresh();
          }, 100);
        });
      });
    },
    toolbarPreparing(e) {
      var toolbarItems = e.toolbarOptions.items;
      var searchPanel = toolbarItems.find((item) => {
        return item.name === "searchPanel";
      });

      let itemsToRemoveFromToolBar = [
        "searchPanel",
        "revertButton",
        "saveButton",
      ];

      e.toolbarOptions.items = toolbarItems.filter(
        (item) => !itemsToRemoveFromToolBar.includes(item.name)
      );

      searchPanel.location = "after";
      e.toolbarOptions.items.unshift(
        {
          location: "before",
          widget: "dxSelectBox",
          options: {
            width: 200,
            items: [
              {
                value: "Review",
                text: "Review",
              },
              {
                value: "ProductList",
                text: "Product",
              },
              {
                value: "CustomerList",
                text: "Customer",
              },
              {
                value: "GLList",
                text: "General Ledger",
              },
            ],
            displayExpr: "text",
            valueExpr: "value",
            value: this.soDetailsCurrentView,
            visible: this.baseView.mode == "create",
            onValueChanged: (e) => {
              this.soDetailsCurrentView = e.value;
            },
          },
        },
        searchPanel,
        {
          location: "after",
          locateinmenu: "auto",
          widget: "dxButton",
          options: {
            tabIndex: -1,
            icon: this.gridExpand ? "collapse" : "expand",
            onClick: () => {
              this.gridExpand = !this.gridExpand;

              setTimeout(() => {
                this.reSizeGrid();
              }, 100);
            },
          },
        }
      );
    },
    onCellPrepared(e) {
      if (e.rowType == "data") {
        if (e.column.dataField == "Available") {
          if (e.value <= 0) {
            e.cellElement.className = "avail-error";
          } else if (e.value > 0) {
            e.cellElement.className = "avail-no-error";
          }

          if (!this.model.BranchCode) {
            e.cellElement.className = "avail-no-error";
          }
        }
      }

      if (e.rowType === "data" && e.column.command === "expand") {
        if (e.data.IsBatchProduct == "No") {
          e.cellElement.classList.remove("dx-datagrid-expand");
          e.cellElement.childNodes[0].classList.remove(
            "dx-datagrid-group-closed"
          );
        }
      }

      if (e.rowType === "data" && e.column.dataField == "SalesUMQty") {
        if (e.data.IsBatchProduct != "No") {
          if (
            FeatureStorage.checkPermission("AlmondcoDespatchData", "create") &&
            this.almondcoQCData
          ) {
            var TotalWeight = e.row.data.InvoiceProductBatches.find(
              (x) => x.TotalWeight > 0
            );
            if (TotalWeight)
              e.cellElement.classList.remove("gc-editable-cell-color");
          } else e.cellElement.classList.remove("gc-editable-cell-color");
        }
      }

      if (e.rowType === "data" && e.column.dataField == "SalesUM") {
        if (e.data.SalesUMs.length < 2) {
          e.cellElement.classList.remove("gc-editable-cell-color");
        }
      }

      if (this.baseView.mode === "edit") {
        e.cellElement.classList.remove("gc-editable-cell-color");
      }
    },
    onBatchCellPrepared(e) {
      if (this.baseView.mode === "edit") {
        e.cellElement.classList.remove("gc-editable-cell-color");
      }
    },
    onEditingStart(e) {
      if (e.data.PSlipStatus == "C") e.cancel = true;
    },
    onRowCollapsed(e) {
      if (
        FeatureStorage.checkPermission("AlmondcoDespatchData", "create") &&
        this.almondcoQCData
      ) {
        var dataGrid = this.$refs["dx-data-grid-comp"].instance;
        dataGrid.byKey(e.key).then((res) => {
          var TotalWeight = res.InvoiceProductBatches.find(
            (x) => x.TotalWeight > 0
          );

          if (!TotalWeight) {
            res.DespatchData = false;
            res.SalesUMQty = res.OriginalSalesUMQty;

            res = SalesOrderState.setStockPrices(res);
            res = SalesOrderState.setLineTotals(res, this.model);

            dataGrid.refresh(true).then(() => {
              this.setFooterItems(this.localDataSource._array);
            });
          }
        });
      }
    },
    onRowExpanded(e) {
      if (
        FeatureStorage.checkPermission("AlmondcoDespatchData", "create") &&
        this.almondcoQCData
      ) {
        var dataGrid = this.$refs["dx-data-grid-comp"].instance;
        dataGrid.byKey(e.key).then((res) => {
          if (res.DespatchData == false) {
            res.SalesUMQty = 0;

            res = SalesOrderState.setStockPrices(res);
            res = SalesOrderState.setLineTotals(res, this.model);

            dataGrid.refresh(true).then(() => {
              this.setFooterItems(this.localDataSource._array);
            });
          }
        });
      }
    },

    onBatchEditorPreparing(e, parentLine) {
      if (e.editorOptions && e.editorOptions.onItemClick) {
        let func = e.editorOptions.onItemClick;
        if (this.baseView.mode != "review" && e.parentType != "searchPanel")
          e.editorOptions.onItemClick = (event) => {
            func(event, {
              column: {
                dataField: e.dataField,
              },
              data: e.row.data,
              rowIndex: e.row.rowIndex,
              parentLine: parentLine,
            });
          };
      }
      if (e.editorOptions && e.editorOptions.onOpened) {
        let func = e.editorOptions.onOpened;
        if (this.baseView.mode != "review" && e.parentType != "searchPanel")
          e.editorOptions.onOpened = (event) => {
            func(event, parentLine);
          };
      }
    },
    async onBatchRowRemoved(e) {
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      var parentLine = undefined;
      if (this.baseView.mode != "create") {
        await dataGrid.byKey(e.data.InvoiceLineNo).then((res) => {
          parentLine = res;
        });
      } else {
        await dataGrid.byKey(e.data.ParentID).then((res) => {
          parentLine = res;
        });
      }

      if (
        FeatureStorage.checkPermission("AlmondcoDespatchData", "create") &&
        this.almondcoQCData
      ) {
        parentLine.SalesUMQty =
          parentLine.SalesUMQty - e.data.TotalWeight / e.data.SalesUMConversion;

        parentLine = SalesOrderState.setStockPrices(parentLine);
        parentLine = SalesOrderState.setLineTotals(parentLine, this.model);

        if (parentLine.InvoiceProductBatches.length == 0) {
          parentLine.DespatchData = false;
          parentLine.InvoiceProductBatches.push({
            CartonOutstandingQty: 0,
            CartonQty: 0,
            CartonWeight: 0,
            ChangedBy: undefined,
            Code: undefined,
            CreatedBy: undefined,
            DateChanged: undefined,
            DateCreated: undefined,
            DespatchDate: new Date(),
            InvoiceLineNo: parentLine.LineNo,
            LotNo: undefined,
            PackedOnDate: undefined,
            PalletNo: undefined,
            RecordID: undefined,
            TotalWeight: 0,
            WorkOrder: undefined,
            PackSize: parentLine.PackSize,
            SalesUMConversion: parentLine.SalesUMConversion,
          });
        }
      } else {
        parentLine.SalesUMQty = parentLine.SalesUMQty - e.data.SalesUMBatchQty;

        parentLine = SalesOrderState.setStockPrices(parentLine);
        parentLine = SalesOrderState.setLineTotals(parentLine, this.model);

        if (parentLine.InvoiceProductBatches.length == 0) {
          parentLine.InvoiceProductBatches.push({
            BatchNo: undefined,
            ItemCode: parentLine.ItemCode,
            StockUMBatchQty: 0,
            SalesUMBatchQty: 0,
            SalesUMInvoiceConversion: 0,
            SupplierCode: undefined,
            SupplierName: undefined,
            InvoiceLineNo: parentLine.LineNo,
            SalesUMCreditedQty: 0,
            UseByDate: undefined,
            BatchLineNo: 0,
            AvailableQty: 0,
            AllocatedQty: 0,
            ParentID: parentLine.ID,
          });
        }
        dataGrid.saveEditData();

        if (this.baseView.mode == "create") {
          this.updateDataSource(parentLine, undefined, true);
        }
      }
      this.setFooterItems(this.localDataSource._array);
      setTimeout(() => {
        dataGrid.refresh(true);
      }, 10);
    },

    onEditorPreparing(e) {
      if (e.dataField == "SalesUMQty") {
        if (e.row.data.IsBatchProduct != "No") {
          if (
            FeatureStorage.checkPermission("AlmondcoDespatchData", "create") &&
            this.almondcoQCData
          ) {
            var TotalWeight = e.row.data.InvoiceProductBatches.find(
              (x) => x.TotalWeight > 0
            );
            if (TotalWeight) e.editorOptions.disabled = true;
          } else e.editorOptions.disabled = true;
        } else {
          e.editorOptions.disabled = false;
        }
      }
      if (e.dataField == "SalesUM") {
        if (e.row.data.SalesUMs.length < 2) {
          e.editorOptions.readOnly = true;
        } else {
          e.editorOptions.readOnly = false;
        }
      }

      if (e.editorOptions && e.editorOptions.onFocusIn) {
        let func = e.editorOptions.onFocusIn;
        if (this.baseView.mode != "review" && e.parentType != "searchPanel")
          e.editorOptions.onFocusIn = (event) => {
            func(event, {
              column: {
                dataField: e.dataField,
              },
              data: e.row.data,
              rowIndex: e.row.rowIndex,
            });
          };
      }

      if (e.editorOptions && e.editorOptions.onItemClick) {
        let func = e.editorOptions.onItemClick;
        if (this.baseView.mode != "review" && e.parentType != "searchPanel")
          e.editorOptions.onItemClick = (event) => {
            func(event, {
              column: {
                dataField: e.dataField,
              },
              data: e.row.data,
              rowIndex: e.row.rowIndex,
            });
          };
      }
      if (e.editorOptions && e.editorOptions.onKeyDown) {
        let func = e.editorOptions.onKeyDown;
        if (this.baseView.mode != "review" && e.parentType != "searchPanel")
          e.editorOptions.onKeyDown = (event) => {
            func(event, {
              column: {
                dataField: e.dataField,
              },
              data: e.row.data,
              rowIndex: e.row.rowIndex,
            });
          };
      }

      if (
        e.dataField &&
        e.dataField == "ItemCode" &&
        (this.soDetailsCurrentView == "ProductList" ||
          this.soDetailsCurrentView == "GLList") &&
        this.baseView.mode == "create"
      ) {
        e.editorName = "dxSelectBox";
      }
    },
    onContentReady(e) {
      this.addGridNoResults(e, this.noDataMessage());

      if (e.component.getDataSource()) {
        this.gridDataSourceItems = e.component.getDataSource()._items;

        if (this.gridDataSourceItems.length > 0 && this.scrollingOffSet) {
          var scrollComponent = e.component.getScrollable();
          scrollComponent.scrollBy(this.scrollingOffSet);

          this.scrollingOffSet = undefined;
        }
      }
      this.setFooterItems(this.localDataSource._array);
    },
    noDataMessage() {
      let noDataMessageObject = {
        addNew: {},
      };

      if (this.soDetailsCurrentView == "Review") {
        noDataMessageObject.addNew = {
          class: "add-new",
          title: "No Order Lines",
          message: "Get Started, Add an Order Line",
        };
      } else if (this.soDetailsCurrentView == "ProductList") {
        noDataMessageObject.addNew = {
          class: "no-data",
          title: "No Product Lines",
          message:
            "Products must be marked Available for Sale to appear here.  Head over to the Product screen to adjust this setting.",
        };
      } else if (this.soDetailsCurrentView == "CustomerList") {
        noDataMessageObject.addNew = {
          class: "no-data",
          title: "No Order Lines",
          message:
            "Products despatched and invoiced to the customer will show here.  Head over to the Despatch Order screen to despatch and invoice orders for this customer",
        };
      } else {
        noDataMessageObject.addNew = {
          class: "add-new",
          title: "No Order Lines",
          message: "Get Started, Add an Order Line",
        };
      }

      return noDataMessageObject;
    },
    refreshDataGrid() {
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;
      dataGrid.repaint(true);
    },

    reSizeGrid() {
      var dataGrid = this.$refs["dx-data-grid-comp"].instance;

      dataGrid.beginUpdate();
      dataGrid.updateDimensions();
      dataGrid.endUpdate();
    },

    removeCellFocus() {
      var cell = document.querySelectorAll(".key-pad-focused-cell");
      if (cell.length > 0) cell[0].classList.remove("key-pad-focused-cell");
    },

    // Footer
    async setFooterItems(gridItems) {
      if (this.baseView.mode == "create")
        gridItems = this.reviewDataList._array;

      this.getFooterItems
        .filter((x) => (x.hasOwnProperty("visible") ? x.visible : true))
        .forEach((x) => {
          x.value = this.updateValue(x.dataField, gridItems);
          if (x.customCalc) x.value = x.customCalc(this.model, gridItems);
        });
      this.footerItems = this.getFooterItems;
      this.showFooter = true;
    },
    updateValue(dataField, items) {
      let result = 0;
      if (items.length > 0)
        items.forEach((x) => {
          if (x.CheckInclude || this.baseView.mode == "review")
            result += parseFloat(x[dataField]).roundNumber(4);
          else if (x.CheckInclude == undefined)
            result += parseFloat(x[dataField]).roundNumber(4);
        });

      return result;
    },
  },
  watch: {},
};
</script>

<style lang="scss">
.invoice-v2 {
  .dx-scrollable-content {
    min-height: 100% !important;
  }
  .layout-body .dx-scrollable-wrapper .dx-scrollable-content {
    .dx-scrollview-content .content {
      //   padding: 10px;
      .touch-sales-order {
        background-color: white;
        // padding: 10px;
      }
    }

    .header-form-container {
      width: 100%;
      display: inline-flex;

      .header-form {
        width: calc((100% / 3) * 2);
        padding-right: 5px;
        margin-bottom: 0 !important;
      }

      .header-comments {
        width: calc((100% / 3) * 1);
        padding-left: 5px;
      }
    }

    .dx-datagrid .dx-row.dx-data-row.dx-row-lines .key-pad-focused-cell {
      background-color: #d1f7ffba !important;
    }

    .dx-icon-money::before {
      color: #20a4da;
    }

    .class-item-cell-template {
      cursor: pointer;
    }

    .gc-grid-footer-css-v2.t-so-foolter-h {
      position: absolute;
      bottom: 10px;
      width: calc(100% - 20px);
    }

    .dx-datagrid-rowsview {
      thead {
        display: none;
      }

      .gc-editable-cell-color[role="gridcell"]:not(.cell-disabled) {
        background-color: #f8fdfe !important;
      }
      .dx-selection.dx-row:not(.dx-row-focused):not(.dx-row-alt) > td,
      .dx-selection.dx-row:not(.dx-row-focused):not(.dx-row-alt):hover > td {
        background-color: unset;
      }

      // .dx-row.dx-row-alt {
      .dx-selection.dx-row.dx-row-alt:not(.dx-row-focused)
        > td:not(.gc-editable-cell-color),
      .dx-selection.dx-row.dx-row-alt:not(.dx-row-focused):hover
        > td:not(.gc-editable-cell-color),
      .dx-selection.dx-row.dx-row-alt:not(.dx-row-focused) > td.cell-disabled,
      .dx-selection.dx-row.dx-row-alt:not(.dx-row-focused):hover
        > td.cell-disabled {
        background-color: #f5f5f5 !important;
      }
      // }

      .dx-data-row:not(.dx-row-alt).dx-state-hover:not(.dx-selection):not(
          .dx-row-inserted
        ):not(.dx-row-removed):not(.dx-edit-row):not(.dx-row-focused)
        > td:not(.dx-focused) {
        background-color: unset;
      }
    }
    .dx-datagrid
      .dx-datagrid-content
      .dx-datagrid-table
      .dx-row
      .dx-editor-cell:not(:first-child) {
      padding-left: 5px !important;
      padding-right: 5px !important;
    }
  }

  .css-v2.transaction-screen {
    background-color: unset !important;
  }

  .edit-cell-numberbox-field {
    .dx-numberbox.dx-texteditor:not(.dx-state-readonly)::after {
      content: "";
      border-bottom: unset !important;
      background-color: unset !important;
    }
    .dx-numberbox.dx-texteditor.dx-state-readonly {
      border-bottom-style: unset !important;
      border-bottom-width: unset !important;
    }
  }

  .qty-cell:not([role="columnheader"])[role="gridcell"] {
    border-left: 3px solid white !important;
    border-right: 3px solid white !important;
  }
  .dx-datagrid .dx-row-alt > td.dx-cell-modified {
    background-color: #f5f5f5 !important;
  }

  .pslip-status-cell {
    text-align: center !important;
    .pslip-status {
      font-size: 18px;
    }

    .grey {
      color: #c7c7c7;
    }

    .orange {
      color: #ffa500;
    }

    .green {
      color: #00c300;
    }
  }
}

.dx-device-tablet {
  .invoice-v2 {
    .layout-body {
      .gc-header-toolbar .gc-form-header-toolbar {
        .view-toolbar.form-header-toolbar {
          .dx-toolbar-items-container {
            .dx-toolbar-before {
              padding-left: 12px;
            }
            .dx-toolbar-after {
              padding-right: 12px;
            }
          }
        }
      }
    }
  }
}

.popup-in-header {
  padding: 10px 10px 0 10px;
  > div {
    background-color: white;
    > div {
      padding: 10px;
    }
  }
}

.gc-popup-product-details {
  .product-details {
    max-width: 100%;
    max-height: 80vh;
    overflow: auto;
    position: relative;
    -ms-flex: 1 1 auto;
    flex: 1 1 auto;
    padding: 10px;

    .popup-in-header {
      padding: 0 !important;
      padding-bottom: 10px !important;
    }

    .details-row {
      // padding: 10px;
      background-color: white;
    }

    .row {
      align-items: center !important;
      display: flex;
      flex-wrap: wrap;

      .col-6 {
        flex: 0 0 50%;
        max-width: 50%;
        position: relative;
        width: 100%;
        min-height: 1px;
        padding-right: 15px;
        padding-left: 15px;

        input {
          text-align: right;
        }

        .product-primary-image-img {
          text-align: center;
          overflow: auto;

          .no-image {
            border: 1px solid #ece1e1;
            width: 140px;
            height: 140px;
            text-align: center;
            display: inline-flex;

            span {
              width: auto;
              margin: auto;
              display: block;
              float: none;
              max-width: 100%;
            }
          }
        }
      }
    }
  }
}

.toolbar-right-form {
  .dx-form {
    .dx-first-row {
      padding: 0 !important;
    }

    .available-stock-switch {
      .dx-field-item-content > div {
        margin-top: 0 !important;
      }
    }
  }
}

.exchange-rate,
.agent-name,
.DateIn-field {
  padding-right: 0 !important;
}

.sales-order-account-status {
  > .dx-popup-normal {
    border-radius: unset !important;
  }
}
</style>
